import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../../../context/AuthContext";
import { fetchWithAuth } from "../../../../utils/api";
import { swalError, swalSuccess } from "../../../../utils/swalAlert";
import Loader from "../../../ui/Loader";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import "../../../../App.css";


/* ── Animated Score Overlay ──────────────────────────────────────────── */
const ScoreOverlay = ({ scoreData, onViewDetails, onBackToClass }) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const [showContent, setShowContent] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [ringOffset, setRingOffset] = useState(565);

  const { obtained, total, correct, incorrect, unanswered } = scoreData;
  const percentage = total > 0 ? Math.round((obtained / total) * 100) : 0;

  const getColor = (pct) => {
    if (pct >= 80)
      return { primary: "var(--color-3)", bg: "rgba(141, 198, 63, 0.15)" };
    if (pct >= 50)
      return { primary: "var(--color-1)", bg: "rgba(240, 154, 33, 0.15)" };
    return { primary: "var(--color-4)", bg: "rgba(239, 65, 54, 0.15)" };
  };

  const colors = getColor(percentage);

  const getPerformanceLabel = (pct) => {
    if (pct >= 90) return "Outstanding! 🏆";
    if (pct >= 80) return "Excellent! 🌟";
    if (pct >= 70) return "Great Job! 👏";
    if (pct >= 50) return "Good Effort! 👍";
    if (pct >= 30) return "Keep Trying! 💪";
    return "Don't Give Up! 📚";
  };

  useEffect(() => {
    const t1 = setTimeout(() => setShowContent(true), 300);
    const t2 = setTimeout(() => {
      const duration = 1500;
      const start = Date.now();
      const animate = () => {
        const elapsed = Date.now() - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setAnimatedScore(Math.round(obtained * eased * 10) / 10);
        const circumference = 565;
        const offset =
          circumference - (circumference * percentage * eased) / 100;
        setRingOffset(offset);
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }, 800);
    const t3 = setTimeout(() => setShowBreakdown(true), 2000);
    const t4 = setTimeout(() => setShowButtons(true), 2500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [obtained, percentage]);

  return (
    <div className="score-overlay">
      <div className="score-card">
        <div className={`score-card-header ${showContent ? "visible" : ""}`}>
          <i className="fa-solid fa-circle-check checkmark-icon" />
          <h2>Exam Submitted</h2>
        </div>

        <div className={`score-ring-container ${showContent ? "visible" : ""}`}>
          <svg className="score-ring-svg" viewBox="0 0 200 200">
            <circle className="score-ring-bg" cx="100" cy="100" r="90" />
            <circle
              className="score-ring-progress"
              cx="100"
              cy="100"
              r="90"
              style={{
                stroke: colors.primary,
                strokeDasharray: 565,
                strokeDashoffset: ringOffset,
              }}
            />
          </svg>
          <div className="score-ring-center">
            <span
              className="score-ring-value"
              style={{ color: colors.primary }}
            >
              {animatedScore % 1 === 0
                ? animatedScore
                : animatedScore.toFixed(1)}
            </span>
            <span className="score-ring-total">out of {total}</span>
            <span
              className="score-ring-pct"
              style={{ background: colors.bg, color: colors.primary }}
            >
              {percentage}%
            </span>
          </div>
        </div>

        <div
          className={`performance-label ${showContent ? "visible" : ""}`}
          style={{ color: colors.primary }}
        >
          {getPerformanceLabel(percentage)}
        </div>

        <div className={`score-breakdown ${showBreakdown ? "visible" : ""}`}>
          <div className="breakdown-item">
            <span className="bd-icon" style={{ color: "var(--color-3)" }}>
              <i className="fa-solid fa-check" />
            </span>
            <span className="bd-value" style={{ color: "var(--color-3)" }}>
              {correct}
            </span>
            <span className="bd-label">Correct</span>
          </div>
          <div className="breakdown-item">
            <span className="bd-icon" style={{ color: "var(--color-4)" }}>
              <i className="fa-solid fa-xmark" />
            </span>
            <span className="bd-value" style={{ color: "var(--color-4)" }}>
              {incorrect}
            </span>
            <span className="bd-label">Incorrect</span>
          </div>
          <div className="breakdown-item">
            <span className="bd-icon" style={{ color: "var(--gray-400)" }}>
              <i className="fa-solid fa-minus" />
            </span>
            <span className="bd-value" style={{ color: "var(--font-color)" }}>
              {unanswered}
            </span>
            <span className="bd-label">Skipped</span>
          </div>
        </div>

        <div className={`score-actions ${showButtons ? "visible" : ""}`}>
          {onViewDetails && (
            <button
              className="score-btn score-btn-primary"
              onClick={onViewDetails}
            >
              <i className="fa-solid fa-chart-column" />
              Detailed Result
            </button>
          )}
          <button
            className="score-btn score-btn-secondary"
            onClick={onBackToClass}
          >
            <i className="fa-solid fa-arrow-left" />
            Back to Class
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Submission Progress Overlay (Online Exam Load Shedding) ─────────── */
const SUBMISSION_STAGES = [
  { key: "preparing", icon: "fa-solid fa-box-archive", label: "Preparing your answers..." },
  { key: "queuing", icon: "fa-solid fa-server", label: "Queuing submission..." },
  { key: "uploading", icon: "fa-solid fa-cloud-arrow-up", label: "Uploading to server..." },
  { key: "verifying", icon: "fa-solid fa-shield-halved", label: "Verifying integrity..." },
  { key: "grading", icon: "fa-solid fa-chart-simple", label: "Processing results..." },
  { key: "done", icon: "fa-solid fa-circle-check", label: "Submission Complete!" },
];

const SubmissionProgressOverlay = ({ stage, retryAttempt }) => {
  const stageIndex = Math.max(0, SUBMISSION_STAGES.findIndex((s) => s.key === stage));
  const currentStage = SUBMISSION_STAGES[stageIndex] || SUBMISSION_STAGES[0];
  const progressPercent = Math.min(100, ((stageIndex + 1) / SUBMISSION_STAGES.length) * 100);
  const isDone = stage === "done";

  return (
    <div className="sp-overlay">
      <div className="sp-modal">
        {/* Icon */}
        <div className={`sp-icon ${isDone ? "sp-icon-done" : ""}`}>
          <i className={currentStage.icon} />
        </div>

        {/* Label */}
        <h2 className="sp-label">{currentStage.label}</h2>

        {/* Progress bar */}
        <div className="sp-bar-track">
          <div className="sp-bar-fill" style={{ width: `${progressPercent}%` }} />
        </div>

        {/* Steps */}
        <div className="sp-steps">
          {SUBMISSION_STAGES.map((s, i) => (
            <div
              key={s.key}
              className={`sp-dot ${
                i < stageIndex ? "done" : i === stageIndex ? "active" : ""
              }`}
            >
              {i < stageIndex ? (
                <i className="fa-solid fa-check" />
              ) : i === stageIndex ? (
                <i className="fa-solid fa-circle-notch fa-spin" />
              ) : null}
            </div>
          ))}
        </div>

        {/* Safety message */}
        <p className="sp-safe-msg">
          <i className="fa-solid fa-lock" /> Your answers are safe. Please don’t close this window.
        </p>
      </div>
    </div>
  );
};

/* ── Basic Calculator ────────────────────────────────────────────────── */
const BasicCalculator = ({ onClose }) => {
  const [display, setDisplay] = useState("0");
  const [prevVal, setPrevVal] = useState(null);
  const [op, setOp] = useState(null);
  const [fresh, setFresh] = useState(true);
  const [pos, setPos] = useState({ x: -1, y: -1 });
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  useEffect(() => {
    setPos({
      x: Math.max(20, window.innerWidth - 320),
      y: Math.max(20, Math.floor(window.innerHeight / 2 - 220)),
    });
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      setPos({ x: cx - dragOffset.current.x, y: cy - dragOffset.current.y });
    };
    const onUp = () => {
      dragging.current = false;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  const startDrag = (e) => {
    dragging.current = true;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    dragOffset.current = { x: cx - pos.x, y: cy - pos.y };
  };

  const calc = (a, b, o) => {
    switch (o) {
      case "+":
        return a + b;
      case "-":
        return a - b;
      case "*":
        return a * b;
      case "/":
        return b !== 0 ? a / b : NaN;
      default:
        return b;
    }
  };

  const digit = (d) => {
    if (fresh) {
      setDisplay(d === "." ? "0." : d);
      setFresh(false);
    } else {
      if (d === "." && display.includes(".")) return;
      setDisplay(display === "0" && d !== "." ? d : display + d);
    }
  };

  const operator = (nextOp) => {
    const cur = parseFloat(display);
    if (prevVal !== null && !fresh) {
      const result = calc(prevVal, cur, op);
      const r = isNaN(result) || !isFinite(result) ? "Error" : result;
      setDisplay(String(r));
      setPrevVal(typeof r === "number" ? r : null);
    } else {
      setPrevVal(cur);
    }
    setOp(nextOp);
    setFresh(true);
  };

  const equals = () => {
    if (prevVal === null || op === null) return;
    const cur = parseFloat(display);
    const result = calc(prevVal, cur, op);
    const r = isNaN(result) || !isFinite(result) ? "Error" : result;
    setDisplay(String(r));
    setPrevVal(null);
    setOp(null);
    setFresh(true);
  };

  const clear = () => {
    setDisplay("0");
    setPrevVal(null);
    setOp(null);
    setFresh(true);
  };

  const backspace = () => {
    if (display.length <= 1 || display === "Error") {
      setDisplay("0");
      setFresh(true);
    } else {
      setDisplay(display.slice(0, -1));
    }
  };

  const percent = () => {
    const v = parseFloat(display);
    if (!isNaN(v)) {
      setDisplay(String(v / 100));
      setFresh(true);
    }
  };

  const plusMinus = () => {
    if (display !== "0" && display !== "Error") {
      setDisplay(String(parseFloat(display) * -1));
    }
  };

  if (pos.x < 0) return null;

  return (
    <div className="exam-calculator" style={{ left: pos.x, top: pos.y }}>
      <div
        className="calc-header"
        onMouseDown={startDrag}
        onTouchStart={startDrag}
      >
        <span>
          <i
            className="fa-solid fa-calculator"
            style={{ marginRight: "0.4rem" }}
          />
          Calculator
        </span>
        <button className="calc-close" onClick={onClose} type="button">
          <i className="fa-solid fa-xmark" />
        </button>
      </div>
      <div className="calc-display">{display}</div>
      <div className="calc-grid">
        <button className="calc-btn fn" onClick={clear} type="button">
          C
        </button>
        <button className="calc-btn fn" onClick={backspace} type="button">
          ⌫
        </button>
        <button className="calc-btn fn" onClick={percent} type="button">
          %
        </button>
        <button
          className="calc-btn op"
          onClick={() => operator("/")}
          type="button"
        >
          ÷
        </button>
        <button className="calc-btn" onClick={() => digit("7")} type="button">
          7
        </button>
        <button className="calc-btn" onClick={() => digit("8")} type="button">
          8
        </button>
        <button className="calc-btn" onClick={() => digit("9")} type="button">
          9
        </button>
        <button
          className="calc-btn op"
          onClick={() => operator("*")}
          type="button"
        >
          ×
        </button>
        <button className="calc-btn" onClick={() => digit("4")} type="button">
          4
        </button>
        <button className="calc-btn" onClick={() => digit("5")} type="button">
          5
        </button>
        <button className="calc-btn" onClick={() => digit("6")} type="button">
          6
        </button>
        <button
          className="calc-btn op"
          onClick={() => operator("-")}
          type="button"
        >
          −
        </button>
        <button className="calc-btn" onClick={() => digit("1")} type="button">
          1
        </button>
        <button className="calc-btn" onClick={() => digit("2")} type="button">
          2
        </button>
        <button className="calc-btn" onClick={() => digit("3")} type="button">
          3
        </button>
        <button
          className="calc-btn op"
          onClick={() => operator("+")}
          type="button"
        >
          +
        </button>
        <button className="calc-btn fn" onClick={plusMinus} type="button">
          ±
        </button>
        <button className="calc-btn" onClick={() => digit("0")} type="button">
          0
        </button>
        <button className="calc-btn" onClick={() => digit(".")} type="button">
          .
        </button>
        <button className="calc-btn eq" onClick={equals} type="button">
          =
        </button>
      </div>
    </div>
)};

/* ── Helper: Format LaTeX Text ────────────────────────────────────────── */
const formatLatexText = (text) => {
  if (!text || typeof text !== "string") return text;
  let processed = text;
  if (
    !processed.includes("$") &&
    !processed.includes("\\(") &&
    !processed.includes("\\[")
  ) {
    const words = [];
    let current = "";
    let depth = 0;
    for (let i = 0; i < processed.length; i++) {
      const c = processed[i];
      if (c === "{" || c === "(") depth++;
      else if (c === "}" || c === ")") depth = Math.max(0, depth - 1);

      if (c === " " && depth === 0) {
        if (current) words.push(current);
        current = "";
      } else {
        current += c;
      }
    }
    if (current) words.push(current);

    processed = words
      .map((word) => {
        if (/(\\[a-zA-Z]+|\^|_)/.test(word)) {
          return `$${word}$`;
        }
        return word;
      })
      .join(" ");
  }

  processed = processed.replace(
    /(^|[^\\])\$([^$]+)\$/g,
    (match, before, math) => {
      if (math.includes("\\displaystyle")) return match;
      return `${before}$\\displaystyle ${math}$`;
    }
  );
  
  processed = processed.replace(/\\\((.*?)\\\)/g, (match, math) => {
    if (math.includes("\\displaystyle")) return match;
    return `\\(\\displaystyle ${math}\\)`;
  });

  return processed;
};

/* ── Main Component ────────────────────────────────────────────────── */
const StudentExamMode = () => {
  const { testId } = useParams();
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const initialTestDataRef = useRef(location.state?.testData);

  /* ── Watermark ─────────────────────────────────────────────────────── */
  const watermarkText = useMemo(() => {
    const referenceId = `${user?.referenceId || ""}`.trim();
    return { referenceId };
  }, [user]);
  const navigate = useNavigate();
  const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

  /* ── State ─────────────────────────── */
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [questionTimeLeft, setQuestionTimeLeft] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [warnings, setWarnings] = useState(0);
  const [showWarningBanner, setShowWarningBanner] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(
    () => !!(document.fullscreenElement || document.webkitFullscreenElement)
  );
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [showScoreOverlay, setShowScoreOverlay] = useState(false);
  const [scoreData, setScoreData] = useState(null);
  const [submittedAnswerPaperId, setSubmittedAnswerPaperId] = useState(null);

  // Submission progress overlay (online exam load shedding)
  const [submissionProgress, setSubmissionProgress] = useState({ visible: false, stage: "preparing", retryAttempt: 0 });

  // New exam-mode states
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [markedForReview, setMarkedForReview] = useState(new Set());
  const [visitedQuestions, setVisitedQuestions] = useState(new Set());
  const [showCalculator, setShowCalculator] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [activeSubject, setActiveSubject] = useState(null);
  const [showQuestionPaper, setShowQuestionPaper] = useState(false);
  const [nextBtnCooldown, setNextBtnCooldown] = useState(0);

  // Proctoring UX states
  const [isTimeFrozen, setIsTimeFrozen] = useState(false);
  const [showTimeUpOverlay, setShowTimeUpOverlay] = useState(false);
  const [showViolationModal, setShowViolationModal] = useState(false);
  const [violationReason, setViolationReason] = useState("");
  const [violationLog, setViolationLog] = useState([]);

  // Video proctoring states
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [isRetryingCamera, setIsRetryingCamera] = useState(false);

  // Offline attention popup (shown after sustained offline period)
  const [showOfflinePopup, setShowOfflinePopup] = useState(false);
  const offlineTimerRef = useRef(null);

  // Network health states (purely client-side, zero server load)
  const [networkStatus, setNetworkStatus] = useState("checking"); // "strong" | "fair" | "weak" | "offline" | "checking"
  const [networkLatency, setNetworkLatency] = useState(null); // rtt in ms from navigator.connection
  const networkStatusRef = useRef("checking");

  /* ── Refs ──────────────────────────── */
  const warningsRef = useRef(0);
  const isSubmittingRef = useRef(false);
  const examFinishedRef = useRef(false);
  const lastWarningTimeRef = useRef(0);
  const answersRef = useRef({});
  const handleFinalSubmitRef = useRef(null);
  const violationLogRef = useRef([]);
  const questionTimeTaken = useRef({});
  const lastTickTimeRef = useRef(null);
  const isTimeFrozenRef = useRef(false);
  const examStartTimeRef = useRef(null); // epoch ms when student clicked Start — stable anchor for global timer
  const initializedTestIdRef = useRef(null); // prevent re-fetching and reshuffling
  const lastNavClickTimeRef = useRef(0);

  // Video proctoring refs
  const videoRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const snapshotBufferRef = useRef([]);
  const snapshotIntervalRef = useRef(null);
  const uploadTimeoutRef = useRef(null);
  const isUploadingRef = useRef(false);
  const captureInProgressRef = useRef(false);

  // Callback refs to guarantee stream attachment immediately on mount
  const attachSidebarVideo = useCallback((node) => {
    videoRef.current = node;
    if (node && cameraStreamRef.current && node.srcObject !== cameraStreamRef.current) {
      node.srcObject = cameraStreamRef.current;
    }
  }, []);

  const attachPreviewVideo = useCallback((node) => {
    videoPreviewRef.current = node;
    if (node && cameraStreamRef.current && node.srcObject !== cameraStreamRef.current) {
      node.srcObject = cameraStreamRef.current;
    }
  }, []);

  const [examStarted, setExamStarted] = useState(false);
  const examStartedRef = useRef(false);

  useEffect(() => {
    examStartedRef.current = examStarted;
  }, [examStarted]);

  useEffect(() => {
    isTimeFrozenRef.current = isTimeFrozen;
  }, [isTimeFrozen]);

  // No resume — exam always starts fresh (examStarted stays false until student clicks Start)

  /* ── Keep answersRef in sync with answers state ────────────────────── */
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  /* ── Keep violationLogRef in sync ──────────────────────────────────── */
  useEffect(() => {
    violationLogRef.current = violationLog;
  }, [violationLog]);

  /* ── Time Tracking per Question ────────────────────────────────────── */
  useEffect(() => {
    if (!test || examFinishedRef.current || isSubmittingRef.current || isTimeFrozen || !examStarted) return;
    
    const qNum = test.onlineExamQuestions?.[currentQuestionIndex]?.questionNumber;
    if (!qNum) return;

    lastTickTimeRef.current = Date.now();

    const timerId = setInterval(() => {
      if (examFinishedRef.current || isSubmittingRef.current || isTimeFrozen) {
        clearInterval(timerId);
        return;
      }
      const now = Date.now();
      const delta = (now - lastTickTimeRef.current) / 1000;
      lastTickTimeRef.current = now;
      
      if (!questionTimeTaken.current[qNum]) {
        questionTimeTaken.current[qNum] = 0;
      }
      questionTimeTaken.current[qNum] += delta;
    }, 1000);

    return () => {
      clearInterval(timerId);
    };
  }, [test, currentQuestionIndex, isTimeFrozen, examStarted]);

  /* ── Video Proctoring: Camera Initialization ──────────────────────── */
  useEffect(() => {
    // Wait for the test to be loaded
    if (!test) return;

    // If proctoring is not enabled or it's a practice test, bypass camera request
    if (!test.videoProctoringEnabled || test.isPracticeTest) {
      setCameraReady(true);
      setCameraError("");
      return;
    }

    let mounted = true;

    const initCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
          audio: false,
        });
        if (!mounted) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        cameraStreamRef.current = stream;
        
        // If nodes exist already (e.g. strict mode remount), attach immediately
        if (videoRef.current) videoRef.current.srcObject = stream;
        if (videoPreviewRef.current) videoPreviewRef.current.srcObject = stream;
        
        setCameraReady(true);
        setCameraError("");
      } catch (err) {
        if (!mounted) return;
        setCameraReady(false);
        if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
          setCameraError("Camera permission was denied. Please open your browser and allow camera access to continue with the exam.");
        } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
          setCameraError("No camera found. Please connect a camera and reload.");
        } else {
          setCameraError("Unable to access camera. Please open your browser and allow camera access, then try again.");
        }
        // Only log camera denial as a violation if the exam has already started
        if (examStartedRef.current) {
          const violation = { type: "camera_denied", timestamp: Date.now() };
          setViolationLog((prev) => [...prev, violation]);
        }
      }
    };

    initCamera();

    return () => {
      mounted = false;
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((t) => t.stop());
        cameraStreamRef.current = null;
      }
      if (snapshotIntervalRef.current) {
        clearInterval(snapshotIntervalRef.current);
        snapshotIntervalRef.current = null;
      }
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
        uploadTimeoutRef.current = null;
      }
    };
  }, [test]); // Re-run when test is loaded
 
  /* ── Retry Camera Access ─────────────────────────────────────────── */
  const handleRetryCamera = useCallback(async () => {
    setIsRetryingCamera(true);
    setCameraError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
        audio: false,
      });
      cameraStreamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      if (videoPreviewRef.current) videoPreviewRef.current.srcObject = stream;
      setCameraReady(true);
      setCameraError("");
    } catch (err) {
      setCameraReady(false);
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setCameraError("Camera permission was denied. Please open your browser and allow camera access to continue with the exam.");
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        setCameraError("No camera found. Please connect or enable a camera and reload.");
      } else {
        setCameraError("Unable to access camera. Please open your browser and allow camera access, then try again.");
      }
    } finally {
      setIsRetryingCamera(false);
    }
  }, []);

  /* ── Network Health Monitor (browser APIs, zero server load) ────── */
  const classifyNetwork = useCallback(() => {
    if (!navigator.onLine) {
      setNetworkStatus("offline");
      networkStatusRef.current = "offline";
      setNetworkLatency(null);
      return;
    }

    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn) {
      const type = conn.effectiveType; // "4g", "3g", "2g", "slow-2g"
      const rtt = conn.rtt;            // round-trip ms
      setNetworkLatency(rtt || null);

      let status;
      if (type === "4g" && (rtt == null || rtt < 300)) {
        status = "strong";
      } else if (type === "4g" || type === "3g") {
        status = "fair";
      } else {
        status = "weak"; // 2g or slow-2g
      }
      setNetworkStatus(status);
      networkStatusRef.current = status;
    } else {
      // Fallback: connection API not supported (Firefox/Safari), assume strong if online
      setNetworkStatus("strong");
      networkStatusRef.current = "strong";
      setNetworkLatency(null);
    }
  }, []);

  useEffect(() => {
    // Initial classification
    classifyNetwork();

    // Listen to network quality changes (event-driven, no polling)
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn) {
      conn.addEventListener("change", classifyNetwork);
    }
    window.addEventListener("online", classifyNetwork);
    window.addEventListener("offline", classifyNetwork);

    return () => {
      if (conn) conn.removeEventListener("change", classifyNetwork);
      window.removeEventListener("online", classifyNetwork);
      window.removeEventListener("offline", classifyNetwork);
    };
  }, [classifyNetwork]);

  /* ── Offline Attention Popup — shows after 5s of continuous offline during exam ── */
  useEffect(() => {
    if (!examStarted || examFinishedRef.current) return;

    if (networkStatus === "offline") {
      // Wait 5 seconds of sustained offline before showing the popup
      offlineTimerRef.current = setTimeout(() => {
        setShowOfflinePopup(true);
      }, 5000);
    } else {
      // Connection restored: cancel pending timer and auto-close popup
      if (offlineTimerRef.current) {
        clearTimeout(offlineTimerRef.current);
        offlineTimerRef.current = null;
      }
      setShowOfflinePopup(false);
    }

    return () => {
      if (offlineTimerRef.current) {
        clearTimeout(offlineTimerRef.current);
        offlineTimerRef.current = null;
      }
    };
  }, [networkStatus, examStarted]);

  /* ── Video Proctoring: Upload Single Snapshot ────────────── */
  const flushSnapshotBuffer = useCallback(
    async () => {
      if (isUploadingRef.current) return;
      const buffer = snapshotBufferRef.current;
      if (buffer.length === 0) return;

      isUploadingRef.current = true;
      // Take exactly 1 snapshot per upload call
      const batch = buffer.splice(0, 1);

      try {
        const formData = new FormData();
        formData.append("testId", test?._id || testId);
        formData.append("studentId", user?._id);
        formData.append(
          "capturedTimestamps",
          JSON.stringify(batch.map((s) => s.capturedAt))
        );
        batch.forEach((snapshot) => {
          const fileExt = snapshot.blob?.type === "image/webp" ? "webp" : "jpg";
          formData.append("snapshots", snapshot.blob, `snapshot_${snapshot.capturedAt}.${fileExt}`);
        });

        await fetchWithAuth(
          `${serverUrl}/api/score/upload-proctor-snapshots`,
          {
            method: "POST",
            body: formData,
            credentials: "include",
          }
        );
      } catch (err) {
        // Upload failed — discard snapshots, no retry
      } finally {
        isUploadingRef.current = false;
        // If more snapshots arrived during upload, drain them one by one
        if (snapshotBufferRef.current.length > 0 && !examFinishedRef.current) {
          setTimeout(() => flushSnapshotBuffer(), 2000);
        }
      }
    },
    [test, testId, user, serverUrl]
  );

  /* ── Video Proctoring: Snapshot Capture & Upload ─────────────────────── */
  useEffect(() => {
    if (!cameraReady || !videoRef.current || !test?.videoProctoringEnabled || !examStarted) return;

    // Env-driven capture interval (seconds)
    const CAPTURE_DURATION_SEC = Number(process.env.REACT_APP_SNAPSHOT_CAPTURE_DURATION) || 30;
    const MAX_RANDOM_DELAY_SEC = Number(process.env.REACT_APP_MAX_RANDOM_DELAY) || 10;
    const SECONDS_MULTIPLE = Number(process.env.REACT_APP_SECONDS_MULTIPLE) || 1;

    // Create offscreen canvas for capturing
    const canvas = document.createElement("canvas");
    canvas.width = 640;
    canvas.height = 480;
    canvasRef.current = canvas;

    const TARGET_SIZE = (process.env.REACT_APP_SNAPSHOT_TARGET_SIZE || 30) * 1024; // Target Image Size in Bytes (default 30KB)

    // Detect if browser supports WebP canvas encoding (Safari on iOS/iPadOS falls back to image/jpeg)
    const getSupportedMimeType = () => {
      try {
        const testCanvas = document.createElement("canvas");
        testCanvas.width = 1;
        testCanvas.height = 1;
        return testCanvas.toDataURL("image/webp").startsWith("data:image/webp")
          ? "image/webp"
          : "image/jpeg";
      } catch {
        return "image/jpeg";
      }
    };

    const MIME_TYPE = getSupportedMimeType();

    const compressToTarget = (canvasEl) => {
      return new Promise((resolve) => {
        // First try high quality with supported MIME type
        canvasEl.toBlob(
          (blob) => {
            if (!blob) return resolve(null);
            if (blob.size <= TARGET_SIZE) return resolve(blob);

            // Binary search for the best quality that fits under TARGET_SIZE
            let lo = 0.1, hi = 0.8, bestBlob = null;
            let smallestBlob = blob; // Track smallest seen to fallback to
            let iterations = 0;
            const MAX_ITER = 5;

            const tryQuality = () => {
              if (iterations >= MAX_ITER) {
                return resolve(bestBlob || smallestBlob);
              }
              const mid = (lo + hi) / 2;
              iterations++;
              canvasEl.toBlob(
                (testBlob) => {
                  if (!testBlob) return resolve(bestBlob || smallestBlob);
                  
                  if (testBlob.size < smallestBlob.size) {
                    smallestBlob = testBlob;
                  }

                  if (testBlob.size <= TARGET_SIZE) {
                    bestBlob = testBlob;
                    lo = mid; // try higher quality
                  } else {
                    hi = mid; // try lower quality
                  }
                  tryQuality();
                },
                MIME_TYPE,
                mid
              );
            };
            tryQuality();
          },
          MIME_TYPE,
          0.85
        );
      });
    };

    const captureAndUpload = async () => {
      // Stop capturing after exam ends OR time is up
      if (examFinishedRef.current || isTimeFrozenRef.current || !videoRef.current || !canvasRef.current) return;
      // Prevent concurrent async capture+compress cycles
      if (captureInProgressRef.current) return;
      captureInProgressRef.current = true;

      try {
        const video = videoRef.current;
        const ctx = canvasRef.current.getContext("2d");
        // Only capture if video is actually playing
        if (video.readyState < 2) return;
        ctx.drawImage(video, 0, 0, 640, 480);

        const blob = await compressToTarget(canvasRef.current);
        if (blob && !examFinishedRef.current && !isTimeFrozenRef.current) {
          // Keep only the latest snapshot — prevents buffer accumulation
          // during slow uploads (old unflushed image is replaced, not stacked)
          snapshotBufferRef.current = [{
            blob,
            capturedAt: Date.now(),
          }];

          // Schedule upload after a random delay
          const randomBaseDelaySec = (Math.floor(Math.random() * MAX_RANDOM_DELAY_SEC) + 1);
          const randomDelaySec = randomBaseDelaySec * SECONDS_MULTIPLE;
          const delayMs = randomDelaySec * 1000;

          // Clear previous timeout to prevent leaked timers
          if (uploadTimeoutRef.current) {
            clearTimeout(uploadTimeoutRef.current);
          }
          uploadTimeoutRef.current = setTimeout(() => {
            if (!examFinishedRef.current) {
              flushSnapshotBuffer();
            }
          }, delayMs);
        }
      } finally {
        captureInProgressRef.current = false;
      }
    };

    snapshotIntervalRef.current = setInterval(captureAndUpload, CAPTURE_DURATION_SEC * 1000);

    return () => {
      if (snapshotIntervalRef.current) {
        clearInterval(snapshotIntervalRef.current);
        snapshotIntervalRef.current = null;
      }
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
        uploadTimeoutRef.current = null;
      }
    };
  }, [cameraReady, test, examStarted, flushSnapshotBuffer]);



  /* ── Video Proctoring: Stop Camera ───────────────────────────────── */
  const stopCamera = useCallback(() => {
    if (snapshotIntervalRef.current) {
      clearInterval(snapshotIntervalRef.current);
      snapshotIntervalRef.current = null;
    }
    if (uploadTimeoutRef.current) {
      clearTimeout(uploadTimeoutRef.current);
      uploadTimeoutRef.current = null;
    }
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }
  }, []);


  /* ── Clean up exam variables ───────────────────── */
  const cleanupExamStorage = useCallback(() => {
    examFinishedRef.current = true;
    stopCamera();
    examStartTimeRef.current = null;
  }, [
    stopCamera,
  ]);

  /* ── Submit Handler ────────────────────────────────────────────────── */
  const handleFinalSubmit = useCallback(
    async (isAutoSubmit = false, autoSubmitReason = "") => {
      // Double guard: block if already submitting OR exam is finished
      if (isSubmittingRef.current || examFinishedRef.current) return;

      // Lock immediately — prevent any concurrent call from entering
      isSubmittingRef.current = true;
      examFinishedRef.current = true; // Block warnings/timer from firing anything further
      setIsSubmitting(true);

      const isOnlineExam = !test?.isPracticeTest;

      // Show animated progress overlay for online exams
      if (isOnlineExam) {
        setSubmissionProgress({ visible: true, stage: "preparing", retryAttempt: 0 });
      }

      // Instantly release the camera so the light turns off during the upload phase
      stopCamera();

      // Flush remaining proctoring snapshots before submission
      try {
        await flushSnapshotBuffer();
      } catch (_) {
        // Non-blocking — submission must proceed even if flush fails
      }

      // Read from ref to avoid stale closure on answers
      const submittedAnswers = { ...answersRef.current };
      // Read violation log from ref
      const currentViolationLog = [...violationLogRef.current];

      // Sanitize timeTaken
      const rawTimeTaken = questionTimeTaken.current || {};
      const sanitizedTimeTaken = {};
      Object.keys(rawTimeTaken).forEach((key) => {
        const val = rawTimeTaken[key];
        if (typeof val === "number" && !isNaN(val)) {
          sanitizedTimeTaken[String(key)] = Math.max(0, Math.min(Math.round(val), 86400));
        }
      });

      // ── LOAD-SHEDDING: Random delay for online exams (0–5s) ──────────
      // Spreads simultaneous submissions across a 5-second window
      // to prevent backend overload during mass exam end events.
      if (isOnlineExam) {
        setSubmissionProgress((p) => ({ ...p, stage: "queuing" }));
        const jitterMs = Math.floor(Math.random() * 5000);
        await new Promise((resolve) => setTimeout(resolve, jitterMs));
      }

      // Helper: attempt a single fetch with an AbortController timeout
      const attemptSubmit = async (signal) => {
        const submitUrl = test?.isPracticeTest 
          ? `${serverUrl}/api/practice/submit`
          : `${serverUrl}/api/score/submit-online-exam`;

        const response = await fetchWithAuth(
          submitUrl,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              testId: test?._id,
              studentId: user?._id,
              answers: submittedAnswers,
              timeTaken: sanitizedTimeTaken,
              isAutoSubmit,
              autoSubmitReason: isAutoSubmit
                ? autoSubmitReason || "Auto Submitted"
                : "",
              violationLog: currentViolationLog,
            }),
            credentials: "include",
            signal,
          },
        );
        return response;
      };

      // ── RETRY ENGINE: handles network errors + 500s with jittered backoff ──
      const MAX_RETRIES = 2;
      const TIMEOUT_MS = 45000;

      const executeWithRetry = async () => {
        let lastError = null;

        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          try {
            if (isOnlineExam) {
              setSubmissionProgress((p) => ({
                ...p,
                stage: "uploading",
                retryAttempt: attempt,
              }));
            }

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
            const response = await attemptSubmit(controller.signal);
            clearTimeout(timeoutId);

            // Handle null response (e.g., 401 handled by fetchWithAuth)
            if (!response) return null;

            // ── 500 SERVER ERROR: retry with random backoff ──────────
            if (response.status >= 500 && attempt < MAX_RETRIES) {
              const backoffBase = (attempt + 1) * 2000; // 2s, 4s
              const retryJitter = Math.floor(Math.random() * 5000); // 0–5s random
              const retryDelay = backoffBase + retryJitter;

              if (isOnlineExam) {
                setSubmissionProgress((p) => ({
                  ...p,
                  stage: "queuing",
                  retryAttempt: attempt + 1,
                }));
              }

              await new Promise((resolve) => setTimeout(resolve, retryDelay));
              continue; // retry the loop
            }

            return response;
          } catch (fetchErr) {
            lastError = fetchErr;

            // Retryable: network errors and AbortError (timeout)
            const isRetryable =
              fetchErr.name === "AbortError" ||
              fetchErr.message === "Failed to fetch" ||
              fetchErr.message?.includes("NetworkError") ||
              fetchErr.message?.includes("network");

            if (isRetryable && attempt < MAX_RETRIES) {
              const backoffBase = (attempt + 1) * 2000;
              const retryJitter = Math.floor(Math.random() * 5000);
              const retryDelay = backoffBase + retryJitter;

              if (isOnlineExam) {
                setSubmissionProgress((p) => ({
                  ...p,
                  stage: "queuing",
                  retryAttempt: attempt + 1,
                }));
              }

              await new Promise((resolve) => setTimeout(resolve, retryDelay));
              continue;
            }

            throw fetchErr; // Non-retryable or exhausted retries
          }
        }

        // All retries exhausted
        throw lastError || new Error("Submission failed after all retries.");
      };

      try {
        const response = await executeWithRetry();

        // Handle null response (e.g., 401 handled by fetchWithAuth)
        if (!response) return;

        if (isOnlineExam) {
          setSubmissionProgress((p) => ({ ...p, stage: "verifying" }));
        }

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          const errMsg = errData.message || "Failed to submit exam.";

          // IDEMPOTENT RECOVERY: If the backend says "already submitted",
          // the first attempt DID succeed — treat it as success, not error.
          if (
            response.status === 400 &&
            errMsg.toLowerCase().includes("already been submitted")
          ) {
            cleanupExamStorage();
            if (isOnlineExam) {
              setSubmissionProgress((p) => ({ ...p, stage: "done" }));
              await new Promise((r) => setTimeout(r, 1200));
              setSubmissionProgress({ visible: false, stage: "preparing", retryAttempt: 0 });
            }
            if (document.fullscreenElement) {
              document.exitFullscreen().catch(() => {});
            }
            swalSuccess("Exam submitted successfully!");
            navigate(`/student/class/${test?.classId}`);
            return;
          }

          throw new Error(errMsg);
        }

        if (isOnlineExam) {
          setSubmissionProgress((p) => ({ ...p, stage: "grading" }));
        }

        const responseData = await response.json();
        cleanupExamStorage();

        const canShowResults =
          test?.scorePublishedDate &&
          new Date() >= new Date(test.scorePublishedDate);

        if (canShowResults) {
          const questions = test.onlineExamQuestions || [];
          if (responseData.scoreData) {
            setScoreData({
              ...responseData.scoreData,
              totalQuestions: questions.length,
            });
          } else {
            let totalObtained = 0;
            let totalMax = 0;
            let correctCount = 0;
            let incorrectCount = 0;
            let unansweredCount = 0;

            questions.forEach((q) => {
              const studentAns = submittedAnswers[q.questionNumber] || "";
              const qType = (q.type || "MCQ").toUpperCase();
              const correctAns =
                qType === "FILL-IN-THE-BLANKS" || qType === "NUMERICAL"
                  ? String(q.correctAnswer ?? "")
                  : q.correctOptions && q.correctOptions.length > 0
                    ? q.correctOptions[0]
                    : "";
              totalMax += q.maxScore || 0;
              if (!studentAns) {
                unansweredCount++;
              } else if (
                studentAns.trim().toUpperCase() ===
                correctAns.trim().toUpperCase()
              ) {
                correctCount++;
                totalObtained += q.maxScore || 0;
              } else {
                incorrectCount++;
              }
            });

            setScoreData({
              obtained: totalObtained,
              total: totalMax,
              correct: correctCount,
              incorrect: incorrectCount,
              unanswered: unansweredCount,
              totalQuestions: questions.length,
            });
          }

          setSubmittedAnswerPaperId(responseData.answerPaperId);

          // Show "done" stage briefly then transition to score overlay
          if (isOnlineExam) {
            setSubmissionProgress((p) => ({ ...p, stage: "done" }));
            await new Promise((r) => setTimeout(r, 1200));
            setSubmissionProgress({ visible: false, stage: "preparing", retryAttempt: 0 });
          }

          setShowScoreOverlay(true);
          if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
          }
        } else {
          if (isOnlineExam) {
            setSubmissionProgress((p) => ({ ...p, stage: "done" }));
            await new Promise((r) => setTimeout(r, 1200));
            setSubmissionProgress({ visible: false, stage: "preparing", retryAttempt: 0 });
          }
          if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
          }
          swalSuccess("Exam submitted successfully!");
          navigate(`/student/class/${test?.classId}`);
        }
      } catch (err) {
        setSubmissionProgress({ visible: false, stage: "preparing", retryAttempt: 0 });

        const isNetworkError =
          !navigator.onLine ||
          err.name === "TypeError" ||
          err.message === "Failed to fetch" ||
          err.message?.includes("NetworkError") ||
          err.message?.includes("network");

        const displayMsg =
          err.name === "AbortError"
            ? "Submission timed out. Check your internet connection and tap Submit again."
            : isNetworkError
            ? "No internet connection. Reconnect and tap Submit again."
            : `${err.message}. Do not reload — just tap Submit again.`;

        swalError(displayMsg);
        // Unlock so student can retry on genuine failure
        isSubmittingRef.current = false;
        examFinishedRef.current = false;
        setIsSubmitting(false);
      }
    },
    [test, user, serverUrl, navigate, cleanupExamStorage, flushSnapshotBuffer, stopCamera],
  );

  // Keep handleFinalSubmitRef in sync so timer/warning callbacks always use the latest
  useEffect(() => {
    handleFinalSubmitRef.current = handleFinalSubmit;
  }, [handleFinalSubmit]);

  /* ── Load Test ─────────────────────────────────────────────────────── */
  useEffect(() => {
    const fetchTestAndInitialize = async () => {
      if (initializedTestIdRef.current === testId) return;

      examFinishedRef.current = false;
      try {
        // Use test data from ExamInstructions if passed via navigation state,
        // otherwise fetch from API (fallback for page refresh / direct URL access)
        let data;
        if (initialTestDataRef.current) {
          data = initialTestDataRef.current;
          initialTestDataRef.current = null;
          // Clear navigation state so browser refresh forces a fresh API fetch
          window.history.replaceState({}, document.title);
        } else {
          const response = await fetchWithAuth(
            `${serverUrl}/api/test/get/${testId}`,
            { credentials: "include" },
          );
          if (!response.ok) throw new Error("Failed to load test details.");
          data = await response.json();
        }

        if (data.scheduledDate && new Date() < new Date(data.scheduledDate)) {
          swalError(
            "This exam has not started yet. Please wait until the scheduled start time.",
          );
          navigate(-1);
          return;
        }

        if (!data.isPracticeTest && data.dueDate && new Date() > new Date(data.dueDate)) {
          swalError("This exam window has closed.");
          navigate(-1);
          return;
        }

        // Extract advanced online exam configuration from new schema array
        const advOptions = data.advancedOnlineExamOptions?.[0] || {};
        data.isAdvancedOnlineExam = !!(data.advancedOnlineExamOptions && data.advancedOnlineExamOptions.length > 0);
        data.videoProctoringEnabled = !!advOptions.videoProctoring;
        data.navigationBackToQuestion = advOptions.navigationBackToQuestion !== false && advOptions.navigationBackToQuestion !== "false";
        data.shuffleQuestions = !!advOptions.shuffleQuestions;
        data.shuffleOptions = !!advOptions.shuffleOptions;

        // Fresh state: start from question 1 with no answers
        setAnswers({});
        setMarkedForReview(new Set());
        questionTimeTaken.current = {};
        setCurrentQuestionIndex(0);

        // Reset warnings so stale counts don't carry over
        warningsRef.current = 0;
        setWarnings(0);
        setViolationLog([]);
        violationLogRef.current = [];
        setExamStarted(false);
        examStartedRef.current = false;

        if (data.onlineExamQuestions && data.onlineExamQuestions.length > 0) {
          // 1. Normalize and optionally shuffle options
          const optOrdersToSave = {};

          const normalized = data.onlineExamQuestions.map((q) => {
            let normalizedOptions = (q.options || []).map((opt, idx) => {
              if (typeof opt === "object" && opt !== null) return { ...opt };
              return {
                key: ["A", "B", "C", "D", "E", "F"][idx] || String(idx + 1),
                text: opt,
              };
            });

            // If option shuffling is enabled: fresh Fisher-Yates shuffle
            if (data.shuffleOptions && normalizedOptions.length > 1) {
              const qNum = q.questionNumber;
              const arr = [...normalizedOptions];
              for (let i = arr.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [arr[i], arr[j]] = [arr[j], arr[i]];
              }
              normalizedOptions = arr;
              optOrdersToSave[qNum] = normalizedOptions.map((opt) => opt.key);
            }

            return { ...q, options: normalizedOptions };
          });
          data.onlineExamQuestions = normalized;

          // 2. Optionally shuffle questions strictly within subject boundaries
          if (data.shuffleQuestions) {
            // Group by subjects preserving original subject appearance order
            const subjectGroups = new Map();
            data.onlineExamQuestions.forEach((q) => {
              const subj = q.subject || "General";
              if (!subjectGroups.has(subj)) subjectGroups.set(subj, []);
              subjectGroups.get(subj).push(q);
            });

            const shuffledQuestions = [];
            subjectGroups.forEach((questionsInSubj) => {
              const shuffled = [...questionsInSubj];
              for (let i = shuffled.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
              }
              shuffledQuestions.push(...shuffled);
            });

            data.onlineExamQuestions = shuffledQuestions;
          }
        }

        // Mark first question as visited
        const restoredVisited = new Set();
        const currentQ = data.onlineExamQuestions?.[0];
        if (currentQ) restoredVisited.add(currentQ.questionNumber);
        setVisitedQuestions(restoredVisited);

        setTest(data);
        initializedTestIdRef.current = testId;
      } catch (err) {
        swalError(err.message);
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    if (user && testId) fetchTestAndInitialize();
  }, [
    testId,
    user,
    serverUrl,
    navigate,
  ]);

  /* ── Countdown Timer (Global) ───────────────────────────────────────── */
  // Uses a stable startTime anchor so the timer is immune to useEffect re-fires.
  // currentQuestionIndex is intentionally NOT in the dependency array —
  // the global timer must never restart when the student navigates questions.
  useEffect(() => {
    let timer;
    if (test) {
      let initialDurationMs = Infinity;
      if (test.duration) {
        initialDurationMs = parseFloat(test.duration) * 60 * 1000;
      }

      let dueDateMs = Infinity;
      if (!test.isPracticeTest && test.dueDate) {
        dueDateMs = new Date(test.dueDate).getTime();
      }

      if (initialDurationMs === Infinity && dueDateMs === Infinity) {
        setTimeLeft("No Limit");
        return;
      }

      const formatHMS = (ms) => {
        const clamped = Math.max(0, ms);
        const h = Math.floor(clamped / (1000 * 60 * 60));
        const m = Math.floor((clamped % (1000 * 60 * 60)) / (1000 * 60));
        const s = Math.floor((clamped % (1000 * 60)) / 1000);
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
      };

      if (examStarted) {
        // Use a stable in-memory anchor. On the very first fire after Start,
        // the ref is null → we stamp it to NOW. On any re-fire, we reuse
        // the existing value — the timer never resets.
        if (examStartTimeRef.current === null && initialDurationMs !== Infinity) {
          examStartTimeRef.current = Date.now();
        }
        const startTime = examStartTimeRef.current;

        // Compute absolute end time from the stable anchor
        const endTimeFromDuration = initialDurationMs !== Infinity ? startTime + initialDurationMs : Infinity;
        const effectiveEndTime = Math.min(endTimeFromDuration, dueDateMs);

        const updateTimer = () => {
          if (examFinishedRef.current || isSubmittingRef.current || isTimeFrozenRef.current) return;

          const now = Date.now();
          const distance = effectiveEndTime - now;

          // Global time expired
          if (distance <= 0) {
            setTimeLeft("00:00:00");
            clearInterval(timer);
            if (!isSubmittingRef.current && !examFinishedRef.current) {
              setIsTimeFrozen(true);
              setShowTimeUpOverlay(true);
            }
          } else {
            setTimeLeft(formatHMS(distance));
          }
        };

        updateTimer();
        timer = setInterval(updateTimer, 1000);
      } else {
        // Before exam starts: display the actual available time (capped by dueDate)
        const availableMs = Math.min(
          initialDurationMs,
          dueDateMs !== Infinity ? Math.max(0, dueDateMs - Date.now()) : Infinity,
        );
        setTimeLeft(formatHMS(availableMs));
      }
    }
    return () => clearInterval(timer);
  }, [test, user, examStarted]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Countdown Timer (Per-Question) ─────────────────────────────────── */
  // Separate from the global timer so question navigation only restarts THIS timer.
  useEffect(() => {
    let qTimer;
    if (test && test.isAdvancedOnlineExam && examStarted) {
      const q = test.onlineExamQuestions?.[currentQuestionIndex];
      if (q?.duration) {
        const initialQDurationMs = parseFloat(q.duration) * 60 * 1000;
        const targetQEndTime = Date.now() + initialQDurationMs;

        const formatHMS = (ms) => {
          const clamped = Math.max(0, ms);
          const h = Math.floor(clamped / (1000 * 60 * 60));
          const m = Math.floor((clamped % (1000 * 60 * 60)) / (1000 * 60));
          const s = Math.floor((clamped % (1000 * 60)) / 1000);
          return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
        };

        const updateQTimer = () => {
          if (examFinishedRef.current || isSubmittingRef.current || isTimeFrozenRef.current) return;
          const now = Date.now();
          const qDistance = targetQEndTime - now;

          if (qDistance <= 0) {
            setQuestionTimeLeft("00:00:00");
            clearInterval(qTimer);
          } else {
            setQuestionTimeLeft(formatHMS(qDistance));
          }
        };

        updateQTimer();
        qTimer = setInterval(updateQTimer, 1000);
      } else {
        setQuestionTimeLeft(null);
      }
    } else {
      setQuestionTimeLeft(null);
    }
    return () => clearInterval(qTimer);
  }, [test, currentQuestionIndex, examStarted]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Navigation Handlers ───────────────────────────────────────────── */
  const jumpToQuestion = useCallback(
    (index) => {
      if (!test?.onlineExamQuestions) return;
      
      // Enforce navigation restrictions: when back navigation is disabled, prevent arbitrary question jumping (only allow staying or moving to immediate next)
      if (
        test?.navigationBackToQuestion === false &&
        index !== currentQuestionIndex &&
        index !== currentQuestionIndex + 1
      ) {
        return;
      }

      // Deduct remaining question time from global timer if applicable.
      // Uses a ref-based accumulator so the global timer's stable anchor
      // is never overwritten — eliminating the race condition that caused
      // premature "Time's Up".
      const clamped = Math.max(
        0,
        Math.min(index, test.onlineExamQuestions.length - 1),
      );
      setCurrentQuestionIndex(clamped);
      setLightboxUrl(null);
      setQuestionTimeLeft(null); // Reset to prevent double-skipping
      const q = test.onlineExamQuestions[clamped];
      // Sync active subject tab
      if (q.subject) setActiveSubject(q.subject);
      setVisitedQuestions((prev) => {
        const next = new Set(prev);
        next.add(q.questionNumber);
        return next;
      });
    },
    [test, currentQuestionIndex],
  );

  const prevQTimeLeftRef = useRef(null);

  useEffect(() => {
    prevQTimeLeftRef.current = questionTimeLeft;
  }, [questionTimeLeft]);

  // Auto-advance logic for per-question timer
  useEffect(() => {
    if (questionTimeLeft === "00:00:00" && test && test.isAdvancedOnlineExam) {
       // Only auto-advance if time actually ticked down to 0 while on this question
       if (prevQTimeLeftRef.current !== null && prevQTimeLeftRef.current !== "00:00:00") {
         const totalCount = test.onlineExamQuestions.length;
         if (currentQuestionIndex < totalCount - 1) {
           jumpToQuestion(currentQuestionIndex + 1);
         } else {
           if (!isSubmittingRef.current && !examFinishedRef.current) {
             setIsTimeFrozen(true);
             setShowTimeUpOverlay(true);
           }
         }
       }
    }
  }, [questionTimeLeft, test, currentQuestionIndex, jumpToQuestion]);

  /* ── Fullscreen ────────────────────────────────────────────────────── */
  const requestFullscreen = useCallback(() => {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      setIsFullscreen(true);
      return;
    }
    const el = document.documentElement;
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.userAgent.includes("Mac") && "ontouchend" in document);
    const hasFullscreenAPI =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.mozRequestFullScreen;

    if (!hasFullscreenAPI || isIOS) {
      setIsFullscreen(true);
      return;
    }
    if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => setIsFullscreen(true));
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else if (el.mozRequestFullScreen) {
      el.mozRequestFullScreen();
    }
  }, []);

  /* ── Tab / Window Visibility Detection ─────────────────────────────── */
  const triggerWarning = useCallback(
    (reason = "tab switch") => {
      if (!examStartedRef.current || isSubmittingRef.current || examFinishedRef.current || test?.isPracticeTest) return;
      // Don't trigger warnings if the exam is already frozen (time's up)
      if (isTimeFrozen) return;
      const now = Date.now();
      if (now - lastWarningTimeRef.current < 1000) return;
      lastWarningTimeRef.current = now;

      warningsRef.current += 1;
      const count = warningsRef.current;
      setWarnings(count);

      // Log the violation
      const violation = { type: reason.toLowerCase().replace(/\s+/g, "_"), timestamp: now };
      setViolationLog((prev) => [...prev, violation]);

      // Show acknowledgment modal — student must click "I Understand" to continue
      setViolationReason(reason);
      setShowViolationModal(true);

      // Also flash the warning banner briefly
      setWarningMessage(
        `⚠️ Warning ${count}: Violation detected (${reason}). This has been recorded.`,
      );
      setShowWarningBanner(true);
      setTimeout(() => setShowWarningBanner(false), 5000);
    },
    [isTimeFrozen, test?.isPracticeTest],
  );

  useEffect(() => {
    const onFsChange = () => {
      const isFs = !!(
        document.fullscreenElement || document.webkitFullscreenElement
      );
      setIsFullscreen(isFs);
      if (!isFs && !isSubmittingRef.current && !examFinishedRef.current && !test?.isPracticeTest && examStartedRef.current) {
        // Enforce a violation for exiting fullscreen only after exam has started
        triggerWarning("exited fullscreen");
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    
    // Ensure state matches DOM on mount (in case it changed right after initial render)
    const initialFs = !!(document.fullscreenElement || document.webkitFullscreenElement);
    setIsFullscreen(initialFs);

    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, [test?.isPracticeTest, triggerWarning]);

  useEffect(() => {
    // Only track visibility/blur IF exam has started AND they have entered fullscreen mode.
    // This prevents false positives from browser permission popups (e.g. camera access),
    // password manager popups, etc., before the student clicks 'Start Exam'.
    if (!test || !examStarted || !isFullscreen) return;
    
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") triggerWarning("tab switch");
    };
    const onWindowBlur = () => triggerWarning("window lost focus");

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("blur", onWindowBlur);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, [test, examStarted, isFullscreen, triggerWarning]);

  /* ── DevTools Detection & Freezer ──────────────────────────────────── */
  useEffect(() => {
    if (process.env.REACT_APP_DISABLE_DEBUGGER_VALIDATION === "true") {
      return;
    }
    if (!test || !examStarted) return;

    // Browsers do not allow programmatically closing DevTools.
    // However, we can freeze it using a constant debugger statement,
    // and measure the time it takes to execute to detect if it's open.
    const devtoolsInterval = setInterval(() => {
      if (!examStartedRef.current) return;
      const before = performance.now();
      // Use Function constructor to prevent production minifiers (like Terser) 
      // from automatically stripping out the 'debugger' statement.
      // eslint-disable-next-line no-new-func
      (new Function("debugger"))(); 
      const after = performance.now();

      // If the debugger paused execution, it took more than 100ms
      if (after - before > 100) {
        triggerWarning("Developer Tools Detected");
      }
    }, 1000);

    return () => clearInterval(devtoolsInterval);
  }, [test, examStarted, triggerWarning]);

  /* ── Block dangerous keyboard shortcuts ────────────────────────────── */
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setLightboxUrl((url) => {
          if (url) return null;
          e.preventDefault();
          e.stopPropagation();
          return url;
        });
        setShowCalculator(false);
        setShowSidebar(false);
        setShowQuestionPaper(false);
        return;
      }
      if (
        e.key === "F5" ||
        ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "r")
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      if (
        e.key === "F12" ||
        ((e.ctrlKey || e.metaKey) &&
          e.shiftKey &&
          ["i", "j", "c"].includes(e.key.toLowerCase()))
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "u") {
        e.preventDefault();
        return false;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
        e.preventDefault();
        return false;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        return false;
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key.toLowerCase() === "w" || e.key === "F4")
      ) {
        e.preventDefault();
        return false;
      }
      if (e.altKey && e.key === "F4") {
        e.preventDefault();
        return false;
      }
      if (e.key === "PrintScreen") {
        e.preventDefault();
        navigator.clipboard.writeText("").catch(() => {});
        return false;
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, []);

  /* ── Block context menu, copy, cut, paste, drag, select ────────────── */
  useEffect(() => {
    const block = (e) => e.preventDefault();
    document.addEventListener("contextmenu", block);
    document.addEventListener("copy", block);
    document.addEventListener("cut", block);
    document.addEventListener("paste", block);
    document.addEventListener("dragstart", block);
    document.addEventListener("drop", block);
    document.addEventListener("selectstart", block);
    return () => {
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("copy", block);
      document.removeEventListener("cut", block);
      document.removeEventListener("paste", block);
      document.removeEventListener("dragstart", block);
      document.removeEventListener("drop", block);
      document.removeEventListener("selectstart", block);
    };
  }, []);

  /* ── Beforeunload ──────────────────────────────────────────────────── */
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (examFinishedRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const goToNext = useCallback(() => {
    const total = test?.onlineExamQuestions?.length || 0;
    if (currentQuestionIndex < total - 1)
      jumpToQuestion(currentQuestionIndex + 1);
  }, [test, currentQuestionIndex, jumpToQuestion]);

  const goToPrevious = useCallback(() => {
    if (currentQuestionIndex > 0) jumpToQuestion(currentQuestionIndex - 1);
  }, [currentQuestionIndex, jumpToQuestion]);

  // Auto-scroll the question palette
  useEffect(() => {
    const paletteGrid = document.querySelector(".exam-palette-grid");
    const currentBtn = document.querySelector(".exam-palette-btn.current");
    if (paletteGrid && currentBtn) {
      const gridRect = paletteGrid.getBoundingClientRect();
      const btnRect = currentBtn.getBoundingClientRect();
      // Only scroll if the button is outside the visible area of the grid
      if (btnRect.top < gridRect.top || btnRect.bottom > gridRect.bottom) {
        currentBtn.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [currentQuestionIndex]);

  /* ── 5-Second Cooldown on Question Change when Back Navigation is Disabled ── */
  useEffect(() => {
    if (!test || !examStarted || test.navigationBackToQuestion !== false) {
      setNextBtnCooldown(0);
      return;
    }

    setNextBtnCooldown(5);
    const interval = setInterval(() => {
      setNextBtnCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [currentQuestionIndex, test, examStarted]);

  const handleSaveAndNext = useCallback(() => {
    const now = Date.now();
    // Guard against rapid double-clicks (within 600ms)
    if (now - lastNavClickTimeRef.current < 600) return;
    if (test?.navigationBackToQuestion === false && nextBtnCooldown > 0) return;

    lastNavClickTimeRef.current = now;
    goToNext();
  }, [goToNext, test?.navigationBackToQuestion, nextBtnCooldown]);

  const handleMarkForReviewAndNext = useCallback(() => {
    if (!test?.onlineExamQuestions) return;
    const q = test.onlineExamQuestions[currentQuestionIndex];
    setMarkedForReview((prev) => {
      const next = new Set(prev);
      if (next.has(q.questionNumber)) {
        next.delete(q.questionNumber);
      } else {
        next.add(q.questionNumber);
      }
      return next;
    });
    goToNext();
  }, [test, currentQuestionIndex, goToNext]);

  /* ── Answer Selection ──────────────────────────────────────────────── */
  const handleOptionSelect = (questionNumber, optionLabel) => {
    const newAnswers = { ...answers, [questionNumber]: optionLabel };
    setAnswers(newAnswers);
    setVisitedQuestions((prev) => {
      const next = new Set(prev);
      next.add(questionNumber);
      return next;
    });
  };

  const handleClearSelection = (questionNumber) => {
    const newAnswers = { ...answers };
    delete newAnswers[questionNumber];
    setAnswers(newAnswers);
  };

  /* ── Question Status ───────────────────────────────────────────────── */
  const getQuestionStatus = useCallback(
    (index) => {
      if (!test?.onlineExamQuestions) return "not-visited";
      const q = test.onlineExamQuestions[index];
      const hasAnswer = !!answers[q.questionNumber];
      const isReviewed = markedForReview.has(q.questionNumber);
      const isVisited = visitedQuestions.has(q.questionNumber) || hasAnswer;

      if (hasAnswer && isReviewed) return "review-answered";
      if (isReviewed) return "review";
      if (hasAnswer) return "answered";
      if (isVisited) return "not-answered";
      return "not-visited";
    },
    [test, answers, markedForReview, visitedQuestions],
  );

  /* ── Stats ─────────────────────────────────────────────────────────── */
  const stats = useMemo(() => {
    if (!test?.onlineExamQuestions)
      return { answered: 0, notAnswered: 0, review: 0, notVisited: 0 };
    let answered = 0,
      notAnswered = 0,
      review = 0,
      notVisited = 0;
    test.onlineExamQuestions.forEach((q) => {
      const hasAnswer = !!answers[q.questionNumber];
      const isVisited = visitedQuestions.has(q.questionNumber) || hasAnswer;
      if (hasAnswer) answered++;
      else if (isVisited) notAnswered++;
      else notVisited++;
      if (markedForReview.has(q.questionNumber)) review++;
    });
    return { answered, notAnswered, review, notVisited };
  }, [test, answers, visitedQuestions, markedForReview]);

  /* ── Timer state ──────────────────────────────────────────────────── */
  const timerState = useMemo(() => {
    if (!timeLeft || timeLeft === "No Limit") return "normal";
    const parts = timeLeft.split(":").map(Number);
    if (parts.length < 3) return "normal";
    const totalMins = parts[0] * 60 + parts[1];
    if (totalMins <= 5) return "urgent";
    if (totalMins <= 15) return "warning";
    return "normal";
  }, [timeLeft]);

  /* ── Derived ───────────────────────────────────────────────────────── */
  const totalCount = test?.onlineExamQuestions?.length || 0;
  const questions = useMemo(() => test?.onlineExamQuestions || [], [test]);
  const currentQuestion = questions[currentQuestionIndex] || null;
  const isCurrentReviewed = currentQuestion
    ? markedForReview.has(currentQuestion.questionNumber)
    : false;
  const isCurrentAnswered = currentQuestion
    ? !!answers[currentQuestion.questionNumber]
    : false;

  /* ── Subject / Section structure ─────────────────────────────────── */
  const subjectStructure = useMemo(() => {
    if (!questions.length) return { subjects: [], subjectMap: {} };
    const subjectMap = {};
    questions.forEach((q, globalIdx) => {
      const subj = q.subject || "General";
      const sec = q.sectionNumber || "SECTION 1";
      if (!subjectMap[subj]) {
        subjectMap[subj] = {
          sections: {},
          order: Object.keys(subjectMap).length,
          minGlobalIndex: globalIdx,
          maxGlobalIndex: globalIdx,
        };
      } else {
        subjectMap[subj].maxGlobalIndex = globalIdx;
      }
      if (!subjectMap[subj].sections[sec])
        subjectMap[subj].sections[sec] = {
          questions: [],
          order: Object.keys(subjectMap[subj].sections).length,
        };
      subjectMap[subj].sections[sec].questions.push({
        ...q,
        _globalIndex: globalIdx,
      });
    });
    const subjects = Object.keys(subjectMap).sort(
      (a, b) => subjectMap[a].order - subjectMap[b].order,
    );
    return { subjects, subjectMap };
  }, [questions]);

  const hasMultipleSubjects = subjectStructure.subjects.length > 1;

  // Auto-set active subject based on current question
  const effectiveSubject = useMemo(() => {
    if (activeSubject && subjectStructure.subjectMap[activeSubject])
      return activeSubject;
    if (currentQuestion?.subject) return currentQuestion.subject;
    return subjectStructure.subjects[0] || null;
  }, [activeSubject, currentQuestion, subjectStructure]);

  const activeSections = useMemo(() => {
    if (!effectiveSubject || !subjectStructure.subjectMap[effectiveSubject])
      return [];
    const secMap = subjectStructure.subjectMap[effectiveSubject].sections;
    return Object.keys(secMap).sort(
      (a, b) => secMap[a].order - secMap[b].order,
    );
  }, [effectiveSubject, subjectStructure]);

  // Stats per subject
  const subjectStats = useMemo(() => {
    const statsMap = {};
    subjectStructure.subjects.forEach((subj) => {
      let answered = 0,
        total = 0;
      const secMap = subjectStructure.subjectMap[subj].sections;
      Object.values(secMap).forEach((sec) => {
        sec.questions.forEach((q) => {
          total++;
          if (answers[q.questionNumber]) answered++;
        });
      });
      statsMap[subj] = { answered, total };
    });
    return statsMap;
  }, [subjectStructure, answers]);



  /* ── Score overlay ─────────────────────────────────────────────────── */
  if (showScoreOverlay && scoreData) {
    return (
      <ScoreOverlay
        scoreData={scoreData}
        onViewDetails={
          submittedAnswerPaperId
            ? () => navigate(`/student/results/${submittedAnswerPaperId}`)
            : null
        }
        onBackToClass={() => navigate(`/student/class/${test?.classId}`)}
      />
    );
  }

  /* ── Loading ───────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="exam-loading">
        <Loader style={{ left: "0", top: "0" }} />
        <p>Loading your exam...</p>
      </div>
    );
  }

  if (!test) return null;

  /* ── Render ─────────────────────────────────────────────────────────── */
  return (
    <div id="exam-root" className="notranslate" translate="no">
      {/* ── Warning Banner ─────────────────────────────────────────── */}
      {showWarningBanner && (
        <div
          className={`exam-warning-banner ${warnings >= 3 ? "danger" : "warning"}`}
        >
          <i
            className={`fa-solid ${warnings >= 3 ? "fa-ban" : "fa-triangle-exclamation"}`}
            style={{ marginRight: "0.75rem" }}
          />
          {warningMessage}
        </div>
      )}

      {/* ── Camera Permission Gate ─────────────────────────────────── */}
      {!cameraReady && (
        <div className="exam-fullscreen-overlay">
          <div className="exam-syscheck-card exam-camera-gate-card">
            <div className="exam-camera-icon-wrap">
              <i className={`fa-solid ${cameraError ? "fa-video-slash" : "fa-video"}`} />
            </div>
            <h2>{cameraError ? "Camera Access Required" : "Requesting Camera Access"}</h2>

            {cameraError ? (
              <>
                <div className="exam-camera-err-alert">
                  <i className="fa-solid fa-circle-exclamation" style={{ marginTop: "2px", flexShrink: 0 }} />
                  <div>{cameraError}</div>
                </div>

                <p style={{ maxWidth: "460px", textAlign: "center", lineHeight: 1.6, color: "#cbd5e1", margin: "0.5rem 0 1.25rem" }}>
                  Please open your browser settings or tap the lock/tune icon in your address bar to allow camera access for this site, then click retry.
                </p>

                <div className="exam-camera-actions">
                  <button
                    type="button"
                    className="exam-fs-enter-btn"
                    onClick={handleRetryCamera}
                    disabled={isRetryingCamera}
                  >
                    <i
                      className={`fa-solid ${isRetryingCamera ? "fa-spinner fa-spin" : "fa-rotate-right"}`}
                      style={{ marginRight: "0.5rem" }}
                    />
                    {isRetryingCamera ? "Checking Camera..." : "Retry Camera Access"}
                  </button>
                  <button
                    type="button"
                    className="exam-fs-secondary-btn"
                    onClick={() => window.location.reload()}
                  >
                    <i className="fa-solid fa-arrows-rotate" style={{ marginRight: "0.5rem" }} />
                    Reload Page
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ maxWidth: "420px", textAlign: "center", lineHeight: 1.6, margin: "1rem 0" }}>
                  Requesting camera access... Please allow camera permission when prompted by your browser.
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--color-1, #f09a21)", fontSize: "0.9rem" }}>
                  <i className="fa-solid fa-circle-notch fa-spin" />
                  <span>Waiting for permission...</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Fullscreen Overlay — System Check Gate / Re-entry ──────── */}
      {cameraReady && (!examStarted || !isFullscreen) && (
        <div className="exam-fullscreen-overlay">
          {examStarted ? (
            <div className="exam-syscheck-card">
              <h2>Fullscreen Required</h2>
              <p style={{ textAlign: "center", marginBottom: "1.5rem" }}>
                You have exited fullscreen mode. This is recorded as a violation.
                You must return to fullscreen to continue your exam.
              </p>
              <button
                className="exam-fs-enter-btn"
                onClick={requestFullscreen}
              >
                <i className="fa-solid fa-expand" style={{ marginRight: "0.5rem" }} />
                Return to Fullscreen
              </button>
            </div>
          ) : (
            <div className="exam-syscheck-card">
              <h2>System Check</h2>

              {/* Camera Check */}
              {test?.videoProctoringEnabled && (
                <>
                  <div className="exam-camera-check-preview">
                    <video ref={attachPreviewVideo} autoPlay playsInline muted />
                  </div>
                  <div className="exam-syscheck-item passed">
                    <i className="fa-solid fa-circle-check" />
                    <span>Camera is working</span>
                  </div>
                </>
              )}

              {/* Network Check (client-side only, zero server load) */}
              <div className={`exam-syscheck-item ${
                networkStatus === "offline" ? "failed" :
                networkStatus === "checking" ? "checking" : "passed"
              }`}>
                {networkStatus === "checking" ? (
                  <>
                    <i className="fa-solid fa-circle-notch fa-spin" />
                    <span>Checking network...</span>
                  </>
                ) : networkStatus === "offline" ? (
                  <>
                    <i className="fa-solid fa-circle-xmark" />
                    <span>No internet connection</span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-circle-check" />
                    <span>
                      Network {networkStatus === "strong" ? "Strong" : networkStatus === "fair" ? "Fair" : "Weak"}
                      {networkLatency != null && ` · ${networkLatency}ms`}
                    </span>
                  </>
                )}
              </div>

              {/* Warning for weak connection */}
              {networkStatus === "weak" && (
                <p className="exam-syscheck-warning">
                  <i className="fa-solid fa-triangle-exclamation" /> Your connection is slow.
                  The exam will still work, but proctoring images may be delayed.
                </p>
              )}

              <p style={{ maxWidth: "500px", textAlign: "center", lineHeight: 1.5, marginBottom: "1.5rem" }}>
                {test?.videoProctoringEnabled
                  ? "Ensure your face is clearly visible. This exam is proctored and must be taken in fullscreen mode."
                  : "This exam must be taken in fullscreen mode to maintain integrity."}
              </p>

              <button
                className="exam-fs-enter-btn"
                disabled={networkStatus === "offline"}
                onClick={() => {
                  if (!document.fullscreenElement && !document.webkitFullscreenElement) {
                    requestFullscreen();
                  }
                  examStartedRef.current = true;
                  setExamStarted(true);
                }}
              >
                <i
                  className={`fa-solid ${document.fullscreenElement || document.webkitFullscreenElement ? "fa-play" : "fa-expand"}`}
                  style={{ marginRight: "0.5rem" }}
                />
                {document.fullscreenElement || document.webkitFullscreenElement
                  ? "Start Exam"
                  : "Enter Fullscreen & Start Exam"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Exam Header ────────────────────────────────────────────── */}
      <header className="exam-header">
        <div className="exam-header-left">
          <img src="/imgs/logo.svg" alt="grademe-logo" width={120} />
          <div className="exam-header-title">
            <span className="exam-header-testname">{test.testName}</span>
            <span className="exam-header-meta">
              {totalCount} Qs · {test.maxScore} Marks
            </span>
          </div>
        </div>

        <div className="exam-header-center">
          <div className={`exam-timer-box ${timerState}`} title="Total exam time remaining">
            <i className="fa-solid fa-clock" />
            <span>{timeLeft || "00:00:00"}</span>
          </div>
          {warnings > 0 && (
            <div
              className="exam-warnings-pill"
              style={{
                background: warnings >= 2 ? "#ef4136" : "#f09a21",
              }}
            >
              <i className="fa-solid fa-triangle-exclamation" />
              {warnings}
            </div>
          )}
        </div>

        <div className="exam-header-right">
          {/* Network Signal Strength */}
          <div className={`exam-network-badge ${networkStatus}`} title={
            networkStatus === "strong" ? `Strong connection${networkLatency != null ? ` (${networkLatency}ms)` : ""}` :
            networkStatus === "fair" ? `Fair connection${networkLatency != null ? ` (${networkLatency}ms)` : ""}` :
            networkStatus === "weak" ? `Weak connection${networkLatency != null ? ` (${networkLatency}ms)` : ""}` :
            networkStatus === "offline" ? "No internet connection" : "Checking network..."
          }>
            <div className="signal-bars">
              <span className={`bar bar-1 ${["strong","fair","weak"].includes(networkStatus) ? "active" : ""}`} />
              <span className={`bar bar-2 ${["strong","fair"].includes(networkStatus) ? "active" : ""}`} />
              <span className={`bar bar-3 ${networkStatus === "strong" ? "active" : ""}`} />
            </div>
            <span className="network-label">
              {networkStatus === "offline" ? "Offline" :
               networkStatus === "checking" ? "..." :
               networkLatency != null ? `${networkLatency}ms` :
               networkStatus.charAt(0).toUpperCase() + networkStatus.slice(1)}
            </span>
          </div>

          <div className="exam-proctored-badge">
            <i className="fa-solid fa-shield-halved" />
            <span>Proctored</span>
          </div>
          <div className="exam-student-info">
            <i className="fa-solid fa-user-circle" />
            <span>
              {user?.firstName} {user?.lastName}
            </span>
          </div>
        </div>
      </header>

      {/* ── Subject / Section Navigation Bar ────────────────────────── */}
      {hasMultipleSubjects && (
        <div className="exam-subject-nav">
          <div className="exam-subject-tabs">
            {subjectStructure.subjects.map((subj) => {
              const subjInfo = subjectStructure.subjectMap[subj];
              const isCurrentSubj = effectiveSubject === subj;
              const isNavBackDisabled = test?.navigationBackToQuestion === false;
              const isFutureSubject =
                isNavBackDisabled &&
                subjInfo?.minGlobalIndex > currentQuestionIndex;
              const isPastSubject =
                isNavBackDisabled &&
                subjInfo?.maxGlobalIndex < currentQuestionIndex;
              const isTabDisabled = isNavBackDisabled && !isCurrentSubj;

              return (
                <button
                  key={subj}
                  type="button"
                  className={`exam-subject-tab ${isCurrentSubj ? "active" : ""} ${isFutureSubject ? "locked" : ""} ${isTabDisabled ? "disabled" : ""}`}
                  disabled={isTabDisabled || isTimeFrozen}
                  title={
                    isFutureSubject
                      ? "Subject locked until you reach its questions"
                      : isPastSubject
                        ? "Navigation back to previous subjects is disabled"
                        : ""
                  }
                  onClick={() => {
                    if (isTabDisabled) return;
                    setActiveSubject(subj);
                    // Jump to first question of this subject
                    const firstSec = Object.keys(
                      subjectStructure.subjectMap[subj].sections,
                    ).sort(
                      (a, b) =>
                        subjectStructure.subjectMap[subj].sections[a].order -
                        subjectStructure.subjectMap[subj].sections[b].order,
                    )[0];
                    if (firstSec) {
                      const firstQ =
                        subjectStructure.subjectMap[subj].sections[firstSec]
                          .questions[0];
                      if (firstQ) jumpToQuestion(firstQ._globalIndex);
                    }
                  }}
                >
                  <span className="exam-subject-tab-name">
                    {subj}
                    {isFutureSubject && (
                      <i
                        className="fa-solid fa-lock"
                        style={{ marginLeft: "0.35rem", fontSize: "0.72rem" }}
                      />
                    )}
                  </span>
                  <span className="exam-subject-tab-count">
                    {subjectStats[subj]?.answered || 0}/
                    {subjectStats[subj]?.total || 0}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Exam Body ──────────────────────────────────────────────── */}
      <div
        className={`exam-layout ${hasMultipleSubjects ? "with-subject-nav" : ""}`}
      >
        {/* ── Left: Question Panel ───────────────────────────────── */}
        <main className="exam-main-panel">
          {currentQuestion ? (
            <>
              {/* Question Top Bar */}
              <div className="exam-q-topbar">
                <div className="exam-q-number-badge">
                  <span className="exam-q-num">
                    Q{currentQuestionIndex + 1}
                  </span>
                  <span className="exam-q-of">of {totalCount}</span>
                  {currentQuestion.sectionNumber && (
                    <span className="exam-q-section-tag">
                      {currentQuestion.sectionNumber}
                    </span>
                  )}
                  {questionTimeLeft && (
                    <span className="exam-q-timer-tag" style={{ marginLeft: "0.5rem", background: "#ffeb3b", color: "#333", padding: "0.25rem 0.5rem", borderRadius: "4px", fontSize: "0.85rem", fontWeight: "600", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                      <i className="fa-solid fa-hourglass-half" />
                      {questionTimeLeft}
                    </span>
                  )}
                </div>
                <div className="exam-q-topbar-right">
                  {test?.navigationBackToQuestion !== false && isCurrentReviewed && (
                    <span className="exam-q-review-tag">
                      <i className="fa-solid fa-bookmark" /> Review
                    </span>
                  )}
                  {currentQuestion.type &&
                    (() => {
                      const qt = currentQuestion.type.toUpperCase();
                      const typeMeta =
                        qt === "NUMERICAL"
                          ? {
                              label: "Numerical",
                              icon: "fa-calculator",
                              bg: "#fef3c7",
                              color: "#92400e",
                            }
                          : qt === "FILL-IN-THE-BLANKS"
                            ? {
                                label: "Fill-in-the-Blank",
                                icon: "fa-pencil",
                                bg: "#dcfce7",
                                color: "#166534",
                              }
                            : {
                                label: "MCQ",
                                icon: "fa-list-ul",
                                bg: "#e0e7ff",
                                color: "#3730a3",
                              };
                      return (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.3rem",
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            padding: "3px 10px",
                            borderRadius: "10px",
                            background: typeMeta.bg,
                            color: typeMeta.color,
                            letterSpacing: "0.02em",
                          }}
                        >
                          <i
                            className={`fa-solid ${typeMeta.icon}`}
                            style={{ fontSize: "0.65rem" }}
                          />
                          {typeMeta.label}
                        </span>
                      );
                    })()}
                  <span className="exam-q-marks-badge">
                    {currentQuestion.maxScore}{" "}
                    {currentQuestion.maxScore === 1 ? "Mark" : "Marks"}
                  </span>
                </div>
              </div>

              {/* Question Content Wrapper */}
              <div className="exam-q-content-wrapper">
                {/* ── Anti-Screenshot Watermark scoped to question area */}
                {!test.isPracticeTest && (
                  <div className="exam-watermark-container">
                    <div className="exam-watermark" aria-hidden="true">
                      {Array.from({ length: 1500 }).map((_, i) => (
                        <span key={i} className="exam-watermark-item">
                          {watermarkText.referenceId}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="exam-q-content">
                  <div className="exam-q-text">
                    <ReactMarkdown
                      remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                      rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                    >
                      {formatLatexText(currentQuestion.questionText)}
                    </ReactMarkdown>
                  </div>

                  {/* Diagram */}
                  {currentQuestion.diagramFileURL && (
                    <div className="exam-q-diagram">
                      <div
                        className="exam-q-diagram-img-wrap"
                        onClick={() =>
                          setLightboxUrl(currentQuestion.diagramFileURL)
                        }
                        title="Click to enlarge"
                      >
                        <img
                          src={currentQuestion.diagramFileURL}
                          alt={`Diagram for Q${currentQuestionIndex + 1}`}
                          draggable={false}
                        />
                      </div>
                      <span className="exam-q-diagram-hint">
                        <i className="fa-solid fa-magnifying-glass" /> Click to
                        enlarge
                      </span>
                    </div>
                  )}

                  {/* Options / Answer Input — type-aware */}
                  {(() => {
                    const qType = (currentQuestion.type || "MCQ").toUpperCase();

                    /* ── Fill-in-the-Blanks ──────────────────────────── */
                    if (qType === "FILL-IN-THE-BLANKS") {
                      const currentVal =
                        answers[currentQuestion.questionNumber] || "";
                      return (
                        <div style={{ marginTop: "1.5rem" }}>
                          <label
                            style={{
                              display: "block",
                              fontSize: "0.82rem",
                              fontWeight: 600,
                              color: "#166534",
                              marginBottom: "0.5rem",
                              letterSpacing: "0.01em",
                            }}
                          >
                            <i
                              className="fa-solid fa-pencil"
                              style={{ marginRight: "0.4rem" }}
                            />
                            Type your answer below
                          </label>
                          <input
                            type="text"
                            value={currentVal}
                            onChange={(e) =>
                              handleOptionSelect(
                                currentQuestion.questionNumber,
                                e.target.value,
                              )
                            }
                            placeholder="Enter your answer here..."
                            autoComplete="off"
                            disabled={isTimeFrozen || questionTimeLeft === "00:00:00"}
                            style={{
                              width: "100%",
                              maxWidth: "480px",
                              padding: "0.75rem 1rem",
                              fontSize: "1rem",
                              border: "2px solid #bbf7d0",
                              borderRadius: "10px",
                              outline: "none",
                              background: "#f0fdf4",
                              color: "#14532d",
                              boxSizing: "border-box",
                              transition: "border-color 0.2s",
                            }}
                            onFocus={(e) =>
                              (e.target.style.borderColor = "#16a34a")
                            }
                            onBlur={(e) =>
                              (e.target.style.borderColor = "#bbf7d0")
                            }
                          />
                        </div>
                      );
                    }

                    /* ── Numerical ───────────────────────────────────── */
                    if (qType === "NUMERICAL") {
                      const currentVal =
                        answers[currentQuestion.questionNumber] ?? "";
                      return (
                        <div style={{ marginTop: "1.5rem" }}>
                          <label
                            style={{
                              display: "block",
                              fontSize: "0.82rem",
                              fontWeight: 600,
                              color: "#92400e",
                              marginBottom: "0.5rem",
                              letterSpacing: "0.01em",
                            }}
                          >
                            <i
                              className="fa-solid fa-calculator"
                              style={{ marginRight: "0.4rem" }}
                            />
                            Enter numerical answer
                          </label>
                          <input
                            type="number"
                            value={currentVal}
                            onChange={(e) =>
                              handleOptionSelect(
                                currentQuestion.questionNumber,
                                e.target.value,
                              )
                            }
                            placeholder="0"
                            autoComplete="off"
                            step="any"
                            disabled={isTimeFrozen || questionTimeLeft === "00:00:00"}
                            style={{
                              width: "100%",
                              maxWidth: "280px",
                              padding: "0.75rem 1rem",
                              fontSize: "1.1rem",
                              fontWeight: 600,
                              border: "2px solid #fde68a",
                              borderRadius: "10px",
                              outline: "none",
                              background: "#fffbeb",
                              color: "#78350f",
                              boxSizing: "border-box",
                              transition: "border-color 0.2s",
                            }}
                            onFocus={(e) =>
                              (e.target.style.borderColor = "#d97706")
                            }
                            onBlur={(e) =>
                              (e.target.style.borderColor = "#fde68a")
                            }
                          />
                          <p
                            style={{
                              marginTop: "0.4rem",
                              fontSize: "0.75rem",
                              color: "#a16207",
                            }}
                          >
                            <i
                              className="fa-solid fa-circle-info"
                              style={{ marginRight: "0.3rem" }}
                            />
                            Decimals are allowed. Enter the exact numerical
                            value.
                          </p>
                        </div>
                      );
                    }

                    /* ── MCQ (default) ───────────────────────────────── */
                    return (
                      <div className="exam-q-options">
                        {(currentQuestion.options || [])
                          .filter(Boolean)
                          .map((opt, optIndex) => {
                            const optKey =
                              opt && typeof opt === "object"
                                ? opt.key
                                : ["A", "B", "C", "D", "E", "F"][optIndex] || String(optIndex + 1);
                            const optText =
                              opt && typeof opt === "object" ? opt.text : opt;
                            if (!optText) return null;
                            const displayLetter = ["A", "B", "C", "D", "E", "F"][optIndex] || String(optIndex + 1);
                            const isSelected =
                              answers[currentQuestion.questionNumber] ===
                              optKey;

                            return (
                              <button
                                key={optKey}
                                type="button"
                                className={`exam-q-option ${isSelected ? "selected" : ""}`}
                                disabled={isTimeFrozen || questionTimeLeft === "00:00:00"}
                                onClick={() =>
                                  handleOptionSelect(
                                    currentQuestion.questionNumber,
                                    optKey,
                                  )
                                }
                              >
                                <span className="exam-q-option-letter">
                                  {displayLetter}
                                </span>
                                <span className="exam-q-option-text">
                                  <ReactMarkdown
                                    remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                                    rehypePlugins={[
                                      [rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }],
                                    ]}
                                  >
                                    {formatLatexText(optText)}
                                  </ReactMarkdown>
                                </span>
                              </button>
                            );
                          })}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Action Bar */}
              <div className="exam-action-bar">
                <div className="exam-action-left">
                  {test?.navigationBackToQuestion !== false && (
                    <button
                      type="button"
                      className={`exam-btn exam-btn-review ${isCurrentReviewed ? "active" : ""}`}
                      onClick={handleMarkForReviewAndNext}
                      disabled={isTimeFrozen || questionTimeLeft === "00:00:00"}
                    >
                      <i className={`fa-solid fa-bookmark`} />
                      {isCurrentReviewed
                        ? "Unmark Review & Next"
                        : "Mark for Review & Next"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="exam-btn exam-btn-clear"
                    onClick={() =>
                      handleClearSelection(currentQuestion.questionNumber)
                    }
                    disabled={!isCurrentAnswered || isTimeFrozen || questionTimeLeft === "00:00:00"}
                  >
                    <i className="fa-solid fa-eraser" />
                    Clear Response
                  </button>
                </div>
                <div className="exam-action-right">
                  {test?.navigationBackToQuestion !== false && (
                    <button
                      type="button"
                      className="exam-btn exam-btn-prev"
                      onClick={goToPrevious}
                      disabled={currentQuestionIndex === 0 || isTimeFrozen}
                    >
                      <i className="fa-solid fa-chevron-left" />
                      Previous
                    </button>
                  )}
                  {currentQuestionIndex >= totalCount - 1 ? (
                    <button
                      type="button"
                      className="exam-btn exam-btn-save"
                      onClick={() => {
                        setShowSidebar(false);
                        setShowSummaryModal(true);
                      }}
                      disabled={isTimeFrozen || (test?.navigationBackToQuestion === false && nextBtnCooldown > 0)}
                    >
                      Submit
                      {test?.navigationBackToQuestion === false && nextBtnCooldown > 0
                        ? ` (${nextBtnCooldown}s)`
                        : ""}
                      <i className="fa-solid fa-paper-plane" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="exam-btn exam-btn-save"
                      onClick={handleSaveAndNext}
                      disabled={isTimeFrozen || (test?.navigationBackToQuestion === false && nextBtnCooldown > 0)}
                    >
                      Save &amp; Next
                      <i className="fa-solid fa-chevron-right" />
                    </button>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="exam-empty-state">
              <i className="fa-solid fa-circle-exclamation" />
              <p>No questions found for this exam.</p>
            </div>
          )}
        </main>

        {/* ── Right: Sidebar ─────────────────────────────────────── */}
        <aside className={`exam-sidebar ${showSidebar ? "open" : ""}`}>
          <button
            className="exam-sidebar-close"
            onClick={() => setShowSidebar(false)}
            type="button"
          >
            <i className="fa-solid fa-xmark" />
          </button>

          {/* Question Palette */}
          <div className="exam-palette-section">
            <div className="exam-palette-header">
              <h4 className="exam-palette-title">Question Palette</h4>
              {test?.navigationBackToQuestion !== false && (
                <button
                  type="button"
                  className="exam-qpaper-btn"
                  onClick={() => {
                    setShowSidebar(false);
                    setShowQuestionPaper(true);
                  }}
                >
                  <i className="fa-solid fa-eye" />
                  Question Paper
                </button>
              )}
            </div>
            {hasMultipleSubjects && effectiveSubject ? (
              <div className="exam-palette-sections-grouped">
                {activeSections.map((sec) => {
                  const secQuestions =
                    subjectStructure.subjectMap[effectiveSubject].sections[sec]
                      .questions;
                  return (
                    <div key={sec} className="exam-palette-section-group">
                      <div className="exam-palette-section-label">{sec}</div>
                      <div className="exam-palette-grid">
                        {secQuestions.map((q) => (
                          <button
                            key={q._globalIndex}
                            type="button"
                            className={`exam-palette-btn ${getQuestionStatus(q._globalIndex)} ${q._globalIndex === currentQuestionIndex ? "current" : ""}`}
                            disabled={isTimeFrozen || (test?.navigationBackToQuestion === false && q._globalIndex !== currentQuestionIndex)}
                            onClick={() => {
                              jumpToQuestion(q._globalIndex);
                              setShowSidebar(false);
                            }}
                          >
                            {q._globalIndex + 1}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="exam-palette-grid">
                {questions.map((q, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`exam-palette-btn ${getQuestionStatus(i)} ${i === currentQuestionIndex ? "current" : ""}`}
                    disabled={isTimeFrozen || (test?.navigationBackToQuestion === false && i !== currentQuestionIndex)}
                    onClick={() => {
                      jumpToQuestion(i);
                      setShowSidebar(false);
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Live Webcam Preview (Sidebar) */}
          {test?.videoProctoringEnabled && !test?.isPracticeTest && (
            <div className="exam-sidebar-webcam">
              <video
                ref={attachSidebarVideo}
                autoPlay
                playsInline
                muted
                className="exam-webcam-video"
              />
              <div className="exam-webcam-indicator">
                <span className="exam-webcam-dot" />
                LIVE PROCTORING
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="exam-palette-legend">
            <div className="legend-item">
              <span className="legend-dot answered" />
              <span>Answered</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot not-answered" />
              <span>Not Answered</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot not-visited" />
              <span>Not Visited</span>
            </div>
            {test?.navigationBackToQuestion !== false && (
              <>
                <div className="legend-item">
                  <span className="legend-dot review" />
                  <span>Marked for Review</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot review-answered" />
                  <span>Answered &amp; Review</span>
                </div>
              </>
            )}
          </div>

          {/* Stats */}
          <div className="exam-palette-stats">
            <div className="stat-item answered">
              <strong>{stats.answered}</strong>
              <span>Answered</span>
            </div>
            <div className="stat-item not-answered">
              <strong>{stats.notAnswered}</strong>
              <span>Not Answered</span>
            </div>
            {test?.navigationBackToQuestion !== false && (
              <div className="stat-item review">
                <strong>{stats.review}</strong>
                <span>Review</span>
              </div>
            )}
            <div className="stat-item not-visited">
              <strong>{stats.notVisited}</strong>
              <span>Not Visited</span>
            </div>
          </div>

          {/* Calculator button */}
          {test.isCalculatorAllowed && (
            <button
              type="button"
              className="exam-sidebar-calc-btn"
              onClick={() => setShowCalculator((p) => !p)}
            >
              <i className="fa-solid fa-calculator" />
              {showCalculator ? "Hide Calculator" : "Open Calculator"}
            </button>
          )}

          {/* Submit */}
          <button
            type="button"
            className="exam-sidebar-submit"
            onClick={() => {
              setShowSidebar(false);
              setShowSummaryModal(true);
            }}
            disabled={isSubmitting || isTimeFrozen}
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" /> Submitting...
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane" /> Submit Exam
              </>
            )}
          </button>
        </aside>
      </div>

      {/* ── Mobile Sidebar Toggle ──────────────────────────────────── */}
      {cameraReady && examStarted && isFullscreen && (
        <button
          type="button"
          className="exam-sidebar-toggle"
          onClick={() => setShowSidebar(true)}
        >
          <i className="fa-solid fa-th" />
          <span>
            {stats.answered}/{totalCount}
          </span>
        </button>
      )}

      {/* Mobile Backdrop */}
      {showSidebar && cameraReady && examStarted && isFullscreen && (
        <div
          className="exam-sidebar-backdrop"
          onClick={() => setShowSidebar(false)}
        />
      )}

      {/* ── Calculator ─────────────────────────────────────────────── */}
      {showCalculator && test.isCalculatorAllowed && (
        <BasicCalculator onClose={() => setShowCalculator(false)} />
      )}

      {/* ── Summary Modal ──────────────────────────────────────────── */}
      {showSummaryModal && (
        <div className="exam-summary-overlay">
          <div className="exam-summary-modal">
            <div className="exam-summary-header">
              <i className="fa-solid fa-clipboard-list" />
              <h3>Exam Summary</h3>
            </div>

            <div className="exam-summary-grid">
              <div className="summary-stat total">
                <span className="summary-stat-val">{totalCount}</span>
                <span className="summary-stat-label">Total Questions</span>
              </div>
              <div className="summary-stat answered">
                <span className="summary-stat-val">{stats.answered}</span>
                <span className="summary-stat-label">Answered</span>
              </div>
              <div className="summary-stat not-answered">
                <span className="summary-stat-val">{stats.notAnswered}</span>
                <span className="summary-stat-label">Not Answered</span>
              </div>
              {test?.navigationBackToQuestion !== false && (
                <div className="summary-stat review">
                  <span className="summary-stat-val">{stats.review}</span>
                  <span className="summary-stat-label">Marked for Review</span>
                </div>
              )}
              <div className="summary-stat not-visited">
                <span className="summary-stat-val">{stats.notVisited}</span>
                <span className="summary-stat-label">Not Visited</span>
              </div>
            </div>

            <p className="exam-summary-warning">
              <i className="fa-solid fa-triangle-exclamation" /> Are you sure
              you want to submit? You cannot change your answers after
              submission. Unanswered questions will receive zero marks.
            </p>

            <div className="exam-summary-actions">
              <button
                type="button"
                className="exam-btn exam-btn-cancel"
                onClick={() => setShowSummaryModal(false)}
              >
                <i className="fa-solid fa-arrow-left" /> Go Back
              </button>
              <button
                type="button"
                className="exam-btn exam-btn-submit-final"
                onClick={() => {
                  setShowSummaryModal(false);
                  handleFinalSubmit(false);
                }}
              >
                <i className="fa-solid fa-paper-plane" /> Yes, Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Time's Up Freeze Overlay ──────────────────────────────────── */}
      {showTimeUpOverlay && (
        <div className="exam-timeup-overlay">
          <div className="exam-timeup-modal">
            <div className="exam-timeup-icon">
              <i className="fa-solid fa-clock" />
            </div>
            <h2 className="exam-timeup-title">Time's Up!</h2>
            <p className="exam-timeup-message">
              Your exam time has ended. Review your attempt summary below and
              click Submit to finalize your exam.
            </p>

            <div className="exam-summary-grid">
              <div className="summary-stat total">
                <span className="summary-stat-val">{totalCount}</span>
                <span className="summary-stat-label">Total Questions</span>
              </div>
              <div className="summary-stat answered">
                <span className="summary-stat-val">{stats.answered}</span>
                <span className="summary-stat-label">Answered</span>
              </div>
              <div className="summary-stat not-answered">
                <span className="summary-stat-val">{stats.notAnswered}</span>
                <span className="summary-stat-label">Not Answered</span>
              </div>
              {test?.navigationBackToQuestion !== false && (
                <div className="summary-stat review">
                  <span className="summary-stat-val">{stats.review}</span>
                  <span className="summary-stat-label">Marked for Review</span>
                </div>
              )}
              <div className="summary-stat not-visited">
                <span className="summary-stat-val">{stats.notVisited}</span>
                <span className="summary-stat-label">Not Visited</span>
              </div>
            </div>

            {warnings > 0 && (
              <div className="exam-timeup-violations">
                <i className="fa-solid fa-triangle-exclamation" />
                <span>{warnings} violation(s) recorded during this exam</span>
              </div>
            )}

            <button
              type="button"
              className="exam-btn exam-btn-submit-final exam-timeup-submit"
              onClick={() => {
                setShowTimeUpOverlay(false);
                handleFinalSubmit(true, "Exam Time Up");
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin" /> Submitting...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-paper-plane" /> Submit Exam
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── Violation Acknowledgment Modal ────────────────────────────── */}
      {showViolationModal && (
        <div className="exam-violation-overlay">
          <div className="exam-violation-modal">
            <div className="exam-violation-icon">
              <i className="fa-solid fa-triangle-exclamation" />
            </div>
            <h2 className="exam-violation-title">Violation Detected</h2>
            <div className="exam-violation-reason">
              <span className="exam-violation-reason-label">Reason:</span>
              <span className="exam-violation-reason-value">{violationReason}</span>
            </div>
            <div className="exam-violation-count">
              Warning {warnings}
            </div>
            <p className="exam-violation-message">
              This violation has been recorded and will be visible to your
              instructor. Please stay in the exam window to avoid further
              violations.
            </p>
            <button
              type="button"
              className="exam-btn exam-violation-btn"
              onClick={() => {
                setShowViolationModal(false);
                if (!document.fullscreenElement && !test?.isPracticeTest) {
                  requestFullscreen();
                }
              }}
            >
              <i className="fa-solid fa-check" /> I Understand, Continue Exam
            </button>
          </div>
        </div>
      )}

      {/* ── Question Paper View ──────────────────────────────────────── */}
      {showQuestionPaper && (
        <div className="exam-qpaper-overlay">
          <div className="exam-qpaper-modal">
            {/* Anti-Screenshot Watermark */}
            {!test.isPracticeTest && (
              <div className="exam-watermark-container">
                <div className="exam-watermark" aria-hidden="true">
                  {Array.from({ length: 1500 }).map((_, i) => (
                    <span key={i} className="exam-watermark-item">
                      {watermarkText.referenceId}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Header */}
            <div className="exam-qpaper-header">
              <div className="exam-qpaper-header-left">
                <i className="fa-solid fa-file-lines" />
                <div>
                  <h3>{test.testName}</h3>
                  <span>
                    {totalCount} Questions · {test.maxScore} Marks
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="exam-qpaper-close"
                onClick={() => setShowQuestionPaper(false)}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="exam-qpaper-body">
              {hasMultipleSubjects
                ? subjectStructure.subjects.map((subj) => {
                    const secMap = subjectStructure.subjectMap[subj].sections;
                    const sections = Object.keys(secMap).sort(
                      (a, b) => secMap[a].order - secMap[b].order,
                    );
                    return (
                      <div key={subj} className="exam-qpaper-subject">
                        <div className="exam-qpaper-subject-header">{subj}</div>
                        {sections.map((sec) => (
                          <div key={sec} className="exam-qpaper-section">
                            <div className="exam-qpaper-section-header">
                              {sec}
                            </div>
                            {secMap[sec].questions.map((q) => (
                              <div
                                key={q._globalIndex}
                                className="exam-qpaper-question"
                              >
                                <div className="exam-qpaper-q-header">
                                  <span className="exam-qpaper-q-num">
                                    Q{q._globalIndex + 1}
                                  </span>
                                  <span className="exam-qpaper-q-marks">
                                    {q.maxScore}{" "}
                                    {q.maxScore === 1 ? "Mark" : "Marks"}
                                  </span>
                                  {answers[q.questionNumber] && (
                                    <span className="exam-qpaper-q-answered">
                                      <i className="fa-solid fa-check" />{" "}
                                      Answered
                                    </span>
                                  )}
                                  {!(test?.navigationBackToQuestion === false && q._globalIndex !== currentQuestionIndex) && (
                                    <button
                                      type="button"
                                      className="exam-qpaper-goto"
                                      onClick={() => {
                                        jumpToQuestion(q._globalIndex);
                                        setShowQuestionPaper(false);
                                      }}
                                    >
                                      <i className="fa-solid fa-arrow-right" /> Go
                                      to Q{q._globalIndex + 1}
                                    </button>
                                  )}
                                </div>
                                <div className="exam-qpaper-q-text">
                                  <ReactMarkdown
                                    remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                                    rehypePlugins={[
                                      [rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }],
                                    ]}
                                  >
                                    {formatLatexText(q.questionText)}
                                  </ReactMarkdown>
                                </div>
                                {q.diagramFileURL && (
                                  <div className="exam-qpaper-q-diagram">
                                    <img
                                      src={q.diagramFileURL}
                                      alt={`Diagram Q${q._globalIndex + 1}`}
                                      draggable={false}
                                    />
                                  </div>
                                )}
                                {q.options &&
                                  q.options.filter(Boolean).length > 0 && (
                                    <div className="exam-qpaper-q-options">
                                      {q.options.filter(Boolean).map((opt, optIndex) => {
                                        const optKey =
                                          opt && typeof opt === "object"
                                            ? opt.key
                                            : opt;
                                        const optText =
                                          opt && typeof opt === "object"
                                            ? opt.text
                                            : opt;
                                        if (!optText) return null;
                                        const displayLetter = ["A", "B", "C", "D", "E", "F"][optIndex] || String(optIndex + 1);
                                        return (
                                          <div
                                            key={optKey}
                                            className={`exam-qpaper-q-opt ${answers[q.questionNumber] === optKey ? "selected" : ""}`}
                                          >
                                            <span className="exam-qpaper-opt-key">
                                              {displayLetter}
                                            </span>
                                            <span className="exam-qpaper-opt-text">
                                              <ReactMarkdown
                                                remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                                                rehypePlugins={[
                                                  [
                                                    rehypeKatex,
                                                    { strict: "ignore", macros: { "\\frac": "\\dfrac" } },
                                                  ],
                                                ]}
                                              >
                                                {formatLatexText(optText)}
                                              </ReactMarkdown>
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })
                : questions.map((q, i) => (
                    <div key={i} className="exam-qpaper-question">
                      <div className="exam-qpaper-q-header">
                        <span className="exam-qpaper-q-num">Q{i + 1}</span>
                        {q.sectionNumber && (
                          <span className="exam-q-section-tag">
                            {q.sectionNumber}
                          </span>
                        )}
                        <span className="exam-qpaper-q-marks">
                          {q.maxScore} {q.maxScore === 1 ? "Mark" : "Marks"}
                        </span>
                        {answers[q.questionNumber] && (
                          <span className="exam-qpaper-q-answered">
                            <i className="fa-solid fa-check" /> Answered
                          </span>
                        )}
                        {!(test?.navigationBackToQuestion === false && i !== currentQuestionIndex) && (
                          <button
                            type="button"
                            className="exam-qpaper-goto"
                            onClick={() => {
                              jumpToQuestion(i);
                              setShowQuestionPaper(false);
                            }}
                          >
                            <i className="fa-solid fa-arrow-right" /> Go to Q
                            {i + 1}
                          </button>
                        )}
                      </div>
                      <div className="exam-qpaper-q-text">
                        <ReactMarkdown
                          remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                          rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                        >
                          {formatLatexText(q.questionText)}
                        </ReactMarkdown>
                      </div>
                      {q.diagramFileURL && (
                        <div className="exam-qpaper-q-diagram">
                          <img
                            src={q.diagramFileURL}
                            alt={`Diagram Q${i + 1}`}
                            draggable={false}
                          />
                        </div>
                      )}
                      {q.options && q.options.filter(Boolean).length > 0 && (
                        <div className="exam-qpaper-q-options">
                          {q.options.filter(Boolean).map((opt, optIndex) => {
                            const optKey =
                              opt && typeof opt === "object" ? opt.key : opt;
                            const optText =
                              opt && typeof opt === "object" ? opt.text : opt;
                            if (!optText) return null;
                            const displayLetter = ["A", "B", "C", "D", "E", "F"][optIndex] || String(optIndex + 1);
                            return (
                              <div
                                key={optKey}
                                className={`exam-qpaper-q-opt ${answers[q.questionNumber] === optKey ? "selected" : ""}`}
                              >
                                <span className="exam-qpaper-opt-key">
                                  {displayLetter}
                                </span>
                                <span className="exam-qpaper-opt-text">
                                  <ReactMarkdown
                                    remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                                    rehypePlugins={[
                                      [rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }],
                                    ]}
                                  >
                                    {formatLatexText(optText)}
                                  </ReactMarkdown>
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Offline Attention Popup — shown once per session when student loses connection ── */}
      {showOfflinePopup && (
        <div className="exam-offline-popup-backdrop">
          <div className="exam-offline-popup-card">
            <div className="exam-offline-popup-icon">
              <i className="fa-solid fa-wifi" />
              <div className="exam-offline-popup-icon-slash" aria-hidden="true" />
            </div>
            <h2 className="exam-offline-popup-title">Internet Connection Lost</h2>
            <p className="exam-offline-popup-body">
              Your internet connection has dropped. Please reconnect — your exam may be interrupted.
            </p>
            <button
              type="button"
              className="exam-offline-popup-btn"
              onClick={() => setShowOfflinePopup(false)}
            >
              <i className="fa-solid fa-check" style={{ marginRight: "0.5rem" }} />
              I Understand
            </button>
          </div>
        </div>
      )}

      {/* ── Submission Progress Overlay (Online Exam) ────────────── */}
      {submissionProgress.visible && (
        <SubmissionProgressOverlay
          stage={submissionProgress.stage}
          retryAttempt={submissionProgress.retryAttempt}
        />
      )}

      {/* ── Image Lightbox ─────────────────────────────────────────── */}
      {lightboxUrl && (
        <div className="exam-lightbox" onClick={() => setLightboxUrl(null)}>
          <button
            className="exam-lightbox-close"
            onClick={() => setLightboxUrl(null)}
            type="button"
          >
            <i className="fa-solid fa-xmark" />
          </button>
          <img
            src={lightboxUrl}
            alt="Diagram enlarged view"
            onClick={(e) => e.stopPropagation()}
            draggable={false}
            style={{ 
              backgroundColor: "#fff",
              height: "100dvh",
              width: "100vw",
              objectFit: "contain"
            }}
          />
        </div>
      )}
    </div>
  );
};

export default StudentExamMode;
